import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { DraftSaver } from '../utils/draftSaver';
import './VendorDashboard.css';

// Helper function to determine primary currency and format price
const getPrimaryCurrencyAndPrice = (product) => {
  // Check which currency has a price value (check all currencies and use the one that was actually set)
  const currencies = [
    { key: 'price_sar', currency: 'KSA', symbol: 'KSA' },
    { key: 'price_aed', currency: 'AED', symbol: 'AED' },
    { key: 'price_gbp', currency: 'GBP', symbol: '£' },
    { key: 'price_usd', currency: 'USD', symbol: '$' }
  ];
  
  for (const curr of currencies) {
    const price = product[curr.key];
    const originalPrice = product[`original_${curr.key}`];
    
    if (price && parseFloat(price) > 0) {
      return { 
        currency: curr.currency, 
        symbol: curr.symbol, 
        price: parseFloat(price), 
        originalPrice: originalPrice ? parseFloat(originalPrice) : null 
      };
    }
  }
  
  // Fallback to USD if no price is set
  return { currency: 'USD', symbol: '$', price: 0, originalPrice: null };
};

const VendorDashboard = () => {
  const [vendorInfo, setVendorInfo] = useState(null);
  const [activeSection, setActiveSection] = useState('overview');
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dispatchSettings, setDispatchSettings] = useState({
    dispatch_method: 'store', // 'store' or 'infinity'
    is_active: true
  });
  const [newProduct, setNewProduct] = useState({
    name: '',
    price_usd: '',
    price_gbp: '',
    price_aed: '',
    price_sar: '',
    original_price_usd: '',
    original_price_gbp: '',
    original_price_aed: '',
    original_price_sar: '',
    category: '',
    subcategories: [],
    newSubcategory: '',
    brand: '',
    stock: '',
    sku: '',
    description: '',
    long_description: '',
    image_url: '',
    gallery_images: [],
    video_url: '',
    video_thumbnail: '',
    specifications: {},
    features: [],
    tags: [],
    weight: '',
    weight_unit: 'g',
    dimensions: '',
    dimension_unit: 'cm',
    countries: [],
    color: '',
    material: '',
    warranty: '',
    shipping_info: '',
    return_policy: '',
    is_featured: false,
    is_bestseller: false,
    is_new_arrival: false,
    meta_title: '',
    meta_description: '',
    seo_keywords: '',
    has_variations: false,
    variations: []
  });

  // Draft saver for new product form
  const [draftSaver] = useState(() => new DraftSaver('vendor_new_product', 5000));
  
  // Test function for draft save popup
  const testDraftSavePopup = () => {
    const testDraftSaver = new DraftSaver('vendor_new_product', 5000);
    testDraftSaver.testDraftSavePopup(newProduct);
  };

  // Test function for warning banner
  const testWarningBanner = () => {
    const testDraftSaver = new DraftSaver('vendor_new_product', 5000);
    testDraftSaver.showUnsavedChangesWarning();
  };
  const [hasDraft, setHasDraft] = useState(false);
  const [draftInfo, setDraftInfo] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [viewingProduct, setViewingProduct] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editProduct, setEditProduct] = useState({
    name: '',
    price_usd: '',
    price_gbp: '',
    price_aed: '',
    price_sar: '',
    original_price_usd: '',
    original_price_gbp: '',
    original_price_aed: '',
    original_price_sar: '',
    category: '',
    subcategories: [],
    newSubcategory: '',
    brand: '',
    stock: '',
    sku: '',
    description: '',
    long_description: '',
    image_url: '',
    gallery_images: [],
    video_url: '',
    video_thumbnail: '',
    specifications: {},
    features: [],
    tags: [],
    weight: '',
    weight_unit: 'g',
    dimensions: '',
    dimension_unit: 'cm',
    countries: [],
    color: '',
    material: '',
    warranty: '',
    shipping_info: '',
    return_policy: '',
    is_featured: false,
    is_bestseller: false,
    is_new_arrival: false,
    meta_title: '',
    meta_description: '',
    seo_keywords: '',
    has_variations: false,
    variations: []
  });
  const [galleryImages, setGalleryImages] = useState([]);
  const [specifications, setSpecifications] = useState([{ key: '', value: '' }]);
  const [features, setFeatures] = useState(['']);
  const [tags, setTags] = useState(['']);
  const [variations, setVariations] = useState([]);
  const [variationTypes, setVariationTypes] = useState([]);
  const [notification, setNotification] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
    
    // Check if vendor is logged in
    const vendorId = localStorage.getItem('vendorId');
    const vendorEmail = localStorage.getItem('vendorEmail');
    const vendorBusinessName = localStorage.getItem('vendorBusinessName');

    if (!vendorId || !vendorEmail) {
      navigate('/vendor/login');
      return;
    }

    setVendorInfo({
      id: vendorId,
      email: vendorEmail,
      businessName: vendorBusinessName
    });

    // Load vendor data
    loadVendorData();
  }, [navigate]);

  // Setup draft auto-save and beforeunload when add product section is active
  useEffect(() => {
    if (activeSection === 'add-product') {
      // Load draft if exists
      loadDraftIfExists();
      
      // Start auto-save
      draftSaver.startAutoSave(newProduct, (savedData) => {
        console.log('Auto-saved draft:', savedData);
      });
      
      // Setup beforeunload event
      const cleanupBeforeUnload = draftSaver.setupBeforeUnload(newProduct);
      
      // Setup navigation protection
      const handleBeforeNavigate = (e) => {
        if (draftSaver.hasUnsavedChanges(newProduct)) {
          e.preventDefault();
          draftSaver.showDraftSavePopupManually(newProduct);
        }
      };
      
      // Listen for navigation attempts
      window.addEventListener('beforeunload', handleBeforeNavigate);
      
      return () => {
        draftSaver.stopAutoSave();
        cleanupBeforeUnload();
        window.removeEventListener('beforeunload', handleBeforeNavigate);
      };
    }
  }, [activeSection, newProduct]);

  const loadVendorData = async () => {
    setLoading(true);
    try {
      const vendorId = localStorage.getItem('vendorId');
      
      // Load products for this vendor
      const { data: productsData, error: productsError } = await supabase
        .from('products')
        .select('*')
        .eq('vendor_id', vendorId);

      if (productsError) {
        console.error('Error loading products:', productsError);
      } else {
        setProducts(productsData || []);
      }

      // Load orders for this vendor (if you have vendor_id in orders table)
      const { data: ordersData, error: ordersError } = await supabase
        .from('orders')
        .select('*')
        .eq('vendor_id', vendorId);

      if (ordersError) {
        console.error('Error loading orders:', ordersError);
      } else {
        // Transform orders data to match the expected format
        const transformedOrders = (ordersData || []).map(order => ({
          id: order.order_number,
          customer: `${order.customer_first_name} ${order.customer_last_name}`,
          total: order.total,
          status: order.status,
          date: new Date(order.created_at).toLocaleDateString()
        }));
        setOrders(transformedOrders);
      }

      // Load categories from database
      const { data: categoriesData, error: categoriesError } = await supabase
        .from('categories')
        .select('*')
        .eq('status', 'active')
        .order('name', { ascending: true });

      if (categoriesError) {
        console.error('Error loading categories:', categoriesError);
      } else {
        console.log('Categories loaded for vendor:', categoriesData);
        setCategories(categoriesData || []);
      }

      // Load subcategories from database
      const { data: subcategoriesData, error: subcategoriesError } = await supabase
        .from('subcategories')
        .select('*, categories(name)')
        .eq('status', 'active')
        .order('name', { ascending: true });

      if (subcategoriesError) {
        console.error('Error loading subcategories:', subcategoriesError);
      } else {
        console.log('Subcategories loaded for vendor:', subcategoriesData);
        setSubcategories(subcategoriesData || []);
      }

      // Load dispatch settings for this vendor
      const { data: dispatchData, error: dispatchError } = await supabase
        .from('vendor_dispatch_settings')
        .select('*')
        .eq('vendor_id', vendorId)
        .single();

      if (dispatchError && dispatchError.code !== 'PGRST116') { // PGRST116 is "not found" error
        console.error('Error loading dispatch settings:', dispatchError);
      } else {
        setDispatchSettings(dispatchData || { dispatch_method: 'store', is_active: true });
      }
    } catch (error) {
      console.error('Error loading vendor data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('vendorId');
    localStorage.removeItem('vendorEmail');
    localStorage.removeItem('vendorBusinessName');
    navigate('/vendor/login');
  };

  // Load draft when entering add product section
  const loadDraftIfExists = () => {
    const draftData = draftSaver.loadDraft();
    if (draftData) {
      setNewProduct(draftData);
      setHasDraft(true);
      setDraftInfo(draftSaver.getDraftInfo());
      showNotification('Draft loaded successfully!', 'success');
    }
  };

  // Save draft manually
  const saveDraftManually = () => {
    const success = draftSaver.saveDraft(newProduct);
    if (success) {
      setHasDraft(true);
      setDraftInfo(draftSaver.getDraftInfo());
      showNotification('Draft saved successfully!', 'success');
    } else {
      showNotification('Failed to save draft', 'error');
    }
  };

  // Clear draft
  const clearDraft = () => {
    const success = draftSaver.clearDraft();
    if (success) {
      setHasDraft(false);
      setDraftInfo(null);
      showNotification('Draft cleared successfully!', 'success');
    }
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => {
      setNotification(null);
    }, 4000);
  };

  const handleDispatchSettingsUpdate = async (dispatchMethod) => {
    try {
      const vendorId = localStorage.getItem('vendorId');
      const newSettings = { 
        dispatch_method: dispatchMethod, 
        is_active: true,
        vendor_id: vendorId 
      };

      // Check if settings exist for this vendor
      const { data: existingSettings, error: checkError } = await supabase
        .from('vendor_dispatch_settings')
        .select('*')
        .eq('vendor_id', vendorId)
        .single();

      let result;
      if (existingSettings) {
        // Update existing settings
        result = await supabase
          .from('vendor_dispatch_settings')
          .update(newSettings)
          .eq('vendor_id', vendorId);
      } else {
        // Insert new settings
        result = await supabase
          .from('vendor_dispatch_settings')
          .insert([newSettings]);
      }

      if (result.error) {
        throw result.error;
      }

      setDispatchSettings(newSettings);
      showNotification(`Dispatch method updated to ${dispatchMethod === 'store' ? 'Store Dispatch' : 'Infinity Dispatch'}`, 'success');
    } catch (error) {
      console.error('Error updating dispatch settings:', error);
      showNotification('Error updating dispatch settings. Please try again.', 'error');
    }
  };

  const handleImageUpload = async (file) => {
    try {
      setUploading(true);
      
      // Generate unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2)}.${fileExt}`;
      const filePath = `${fileName}`;

      // Upload to Supabase Storage
      const { data, error } = await supabase.storage
        .from('product-images')
        .upload(filePath, file);

      if (error) {
        throw error;
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('product-images')
        .getPublicUrl(filePath);

      setNewProduct(prev => ({ ...prev, image_url: publicUrl }));
      setUploading(false);
      
    } catch (error) {
      console.error('Upload error:', error);
      showNotification('Error uploading image. Please try again.', 'error');
      setUploading(false);
    }
  };

  const handleVideoUpload = async (file) => {
    try {
      setUploading(true);
      
      // Validate file type
      const allowedTypes = ['video/mp4', 'video/webm', 'video/ogg', 'video/quicktime'];
      if (!allowedTypes.includes(file.type)) {
        throw new Error('Invalid video format. Please upload MP4, WebM, OGG, or MOV files.');
      }

      // Validate file size (max 100MB)
      const maxSize = 100 * 1024 * 1024; // 100MB
      if (file.size > maxSize) {
        throw new Error('Video file is too large. Maximum size is 100MB.');
      }

      // Generate unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2)}.${fileExt}`;
      const filePath = `videos/${fileName}`;

      // Upload to Supabase Storage
      const { data, error } = await supabase.storage
        .from('product-videos')
        .upload(filePath, file);

      if (error) {
        throw error;
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('product-videos')
        .getPublicUrl(filePath);

      setNewProduct(prev => ({ ...prev, video_url: publicUrl }));
      setUploading(false);
      showNotification('Video uploaded successfully!', 'success');
      
    } catch (error) {
      console.error('Video upload error:', error);
      showNotification(error.message || 'Error uploading video. Please try again.', 'error');
      setUploading(false);
    }
  };



  const handleAddProduct = async (e) => {
    e.preventDefault();
    
    // Check if at least one price is provided
    const hasPrice = newProduct.price_usd || newProduct.price_gbp || newProduct.price_aed || newProduct.price_sar;
    
    if (!newProduct.name || !hasPrice || !newProduct.stock) {
      showNotification('Please fill in all required fields (Name, at least one currency price, and Stock are mandatory).', 'error');
      return;
    }

    try {
      const vendorId = localStorage.getItem('vendorId');
      
      // Convert specifications array to object
      const specsObject = {};
      specifications.forEach(spec => {
        if (spec.key && spec.value) {
          specsObject[spec.key] = spec.value;
        }
      });

      // Filter out empty features and tags
      const filteredFeatures = features.filter(feature => feature.trim() !== '');
      const filteredTags = tags.filter(tag => tag.trim() !== '');

      // Helper function to safely parse numeric values
      const parseNumericValue = (value) => {
        if (value === null || value === undefined || value === '' || value === 'undefined' || value === 'null') {
          return null;
        }
        const parsed = parseFloat(value);
        if (isNaN(parsed)) {
          return null;
        }
        return parsed;
      };

      // Helper function to safely parse integer values
      const parseIntValue = (value) => {
        if (value === null || value === undefined || value === '' || value === 'undefined' || value === 'null') {
          return 0;
        }
        const parsed = parseInt(value);
        if (isNaN(parsed)) {
          return 0;
        }
        return parsed;
      };

      const productData = {
        name: newProduct.name.trim(),
        price_usd: parseNumericValue(newProduct.price_usd),
        price_gbp: parseNumericValue(newProduct.price_gbp),
        price_aed: parseNumericValue(newProduct.price_aed),
        price_sar: parseNumericValue(newProduct.price_sar),
        original_price_usd: parseNumericValue(newProduct.original_price_usd),
        original_price_gbp: parseNumericValue(newProduct.original_price_gbp),
        original_price_aed: parseNumericValue(newProduct.original_price_aed),
        original_price_sar: parseNumericValue(newProduct.original_price_sar),
        category: newProduct.category || '',
        subcategory: newProduct.subcategories && newProduct.subcategories.length > 0 ? JSON.stringify(newProduct.subcategories) : null,
        brand: newProduct.brand || '',
        stock: parseIntValue(newProduct.stock),
        sku: newProduct.sku || '',
        description: newProduct.description || '',
        long_description: newProduct.long_description || '',
        image_url: newProduct.image_url || '',
        gallery_images: newProduct.gallery_images || [],
        video_url: newProduct.video_url || '',
        video_thumbnail: newProduct.video_thumbnail || '',
        specifications: specsObject,
        features: filteredFeatures,
        tags: filteredTags,
        weight: parseNumericValue(newProduct.weight),
        weight_unit: newProduct.weight_unit || 'g',
        dimensions: newProduct.dimensions || '',
        dimension_unit: newProduct.dimension_unit || 'cm',
        countries: newProduct.countries || [],
        color: newProduct.color || '',
        material: newProduct.material || '',
        warranty: newProduct.warranty || '',
        shipping_info: newProduct.shipping_info || '',
        return_policy: newProduct.return_policy || '',
        is_featured: newProduct.is_featured || false,
        is_bestseller: newProduct.is_bestseller || false,
        is_new_arrival: newProduct.is_new_arrival || false,
        meta_title: newProduct.meta_title || '',
        meta_description: newProduct.meta_description || '',
        seo_keywords: newProduct.seo_keywords || '',
        has_variations: newProduct.has_variations || false,
        variations: newProduct.has_variations ? variations : [],
        vendor_id: vendorId,
        status: editingProduct ? editingProduct.status : 'active'
      };

      let result;
      if (editingProduct) {
        // Update existing product
        const { data, error } = await supabase
          .from('products')
          .update(productData)
          .eq('id', editingProduct.id)
          .select()
          .single();
        
        if (error) {
          console.error('Error updating product:', error);
          throw error;
        }
        result = data;
        showNotification('Product updated successfully!', 'success');
      } else {
        // Add new product
        console.log('Sending product data to Supabase:', productData);
        const { data, error } = await supabase
          .from('products')
          .insert([productData])
          .select()
          .single();
        
        if (error) {
          console.error('Error adding product:', error);
          console.error('Product data that failed:', productData);
          throw error;
        }
        result = data;
        showNotification('Product added successfully!', 'success');
      }

      // Subcategories are now stored directly in the product data
      // No need for separate table relationships

      // Reset form and go back to products
      setNewProduct({
        name: '',
        price_usd: '',
        price_gbp: '',
        price_aed: '',
        price_sar: '',
        original_price_usd: '',
        original_price_gbp: '',
        original_price_aed: '',
        original_price_sar: '',
        category: '',
        subcategories: [],
        newSubcategory: '',
        brand: '',
        stock: '',
        sku: '',
        description: '',
        long_description: '',
        image_url: '',
        gallery_images: [],
        video_url: '',
        video_thumbnail: '',
        specifications: {},
        features: [],
        tags: [],
        weight: '',
        weight_unit: 'g',
        dimensions: '',
        dimension_unit: 'cm',
        countries: [],
        color: '',
        material: '',
        warranty: '',
        shipping_info: '',
        return_policy: '',
        is_featured: false,
        is_bestseller: false,
        is_new_arrival: false,
        meta_title: '',
        meta_description: '',
        seo_keywords: '',
        has_variations: false,
        variations: []
      });
      setGalleryImages([]);
      setSpecifications([{ key: '', value: '' }]);
      setFeatures(['']);
      setTags(['']);
      setVariations([]);
      setVariationTypes([]);
      setEditingProduct(null);
      setActiveSection('products');
      
      // Clear draft after successful submission
      draftSaver.clearDraft();
      setHasDraft(false);
      setDraftInfo(null);
      
      loadVendorData(); // Reload products
      
    } catch (error) {
      console.error('Error saving product:', error);
      showNotification(`Error ${editingProduct ? 'updating' : 'adding'} product. Please try again.`, 'error');
    }
  };

  const handleEditProduct = (product) => {
    // Populate the newProduct state with the existing product data
    setNewProduct({
      name: product.name || '',
      price_usd: product.price_usd || '',
      price_gbp: product.price_gbp || '',
      price_aed: product.price_aed || '',
      price_sar: product.price_sar || '',
      original_price_usd: product.original_price_usd || '',
      original_price_gbp: product.original_price_gbp || '',
      original_price_aed: product.original_price_aed || '',
      original_price_sar: product.original_price_sar || '',
      category: product.category || '',
      subcategories: product.subcategory ? (() => {
        try {
          return typeof product.subcategory === 'string' ? JSON.parse(product.subcategory) : [product.subcategory];
        } catch (e) {
          console.warn('Error parsing subcategory JSON:', e);
          return [product.subcategory];
        }
      })() : [],
      brand: product.brand || '',
      stock: product.stock || '',
      sku: product.sku || '',
      description: product.description || '',
      long_description: product.long_description || '',
      image_url: product.image_url || '',
      gallery_images: product.gallery_images || [],
      video_url: product.video_url || '',
      video_thumbnail: product.video_thumbnail || '',
      specifications: product.specifications || {},
      features: product.features || [],
      tags: product.tags || [],
      weight: product.weight || '',
      weight_unit: product.weight_unit || 'g',
      dimensions: product.dimensions || '',
      dimension_unit: product.dimension_unit || 'cm',
      countries: product.countries || [],
      color: product.color || '',
      material: product.material || '',
      warranty: product.warranty || '',
      shipping_info: product.shipping_info || '',
      return_policy: product.return_policy || '',
      is_featured: product.is_featured || false,
      is_bestseller: product.is_bestseller || false,
      is_new_arrival: product.is_new_arrival || false,
      meta_title: product.meta_title || '',
      meta_description: product.meta_description || '',
      seo_keywords: product.seo_keywords || '',
      has_variations: product.has_variations || false,
      variations: product.variations || []
    });

    // Set the gallery images, specifications, features, and tags
    setGalleryImages(product.gallery_images || []);
    setSpecifications(product.specifications ? Object.entries(product.specifications).map(([key, value]) => ({ key, value })) : [{ key: '', value: '' }]);
    setFeatures(product.features && product.features.length > 0 ? product.features : ['']);
    setTags(product.tags && product.tags.length > 0 ? product.tags : ['']);
    setVariations(product.variations || []);
    setVariationTypes(product.variations ? product.variations.map(v => v.type) : []);

    // Store the editing product ID for reference
    setEditingProduct(product);

    // Redirect to the add product section
    setActiveSection('add-product');
  };

  const handleViewProduct = (product) => {
    setViewingProduct(product);
    setShowViewModal(true);
  };

  const closeViewModal = () => {
    setShowViewModal(false);
    setViewingProduct(null);
  };

  const handleDeleteProduct = async (product) => {
    if (window.confirm(`Are you sure you want to delete "${product.name}"? This action cannot be undone.`)) {
      try {
        const { error } = await supabase
          .from('products')
          .delete()
          .eq('id', product.id);

        if (error) {
          throw error;
        }

        showNotification('Product deleted successfully!', 'success');
        loadVendorData(); // Reload products
        
      } catch (error) {
        console.error('Error deleting product:', error);
        showNotification('Error deleting product. Please try again.', 'error');
      }
    }
  };

  const handleEditImageUpload = async (file) => {
    try {
      setUploading(true);
      
      // Generate unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2)}.${fileExt}`;
      const filePath = `${fileName}`;

      // Upload to Supabase Storage
      const { data, error } = await supabase.storage
        .from('product-images')
        .upload(filePath, file);

      if (error) {
        throw error;
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('product-images')
        .getPublicUrl(filePath);

      setEditProduct(prev => ({ ...prev, image_url: publicUrl }));
      setUploading(false);
      
    } catch (error) {
      console.error('Upload error:', error);
      showNotification('Error uploading image. Please try again.', 'error');
      setUploading(false);
    }
  };

  const handleEditVideoUpload = async (file) => {
    try {
      setUploading(true);
      
      // Validate file type
      const allowedTypes = ['video/mp4', 'video/webm', 'video/ogg', 'video/quicktime'];
      if (!allowedTypes.includes(file.type)) {
        throw new Error('Invalid video format. Please upload MP4, WebM, OGG, or MOV files.');
      }

      // Validate file size (max 100MB)
      const maxSize = 100 * 1024 * 1024; // 100MB
      if (file.size > maxSize) {
        throw new Error('Video file is too large. Maximum size is 100MB.');
      }

      // Generate unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2)}.${fileExt}`;
      const filePath = `videos/${fileName}`;

      // Upload to Supabase Storage
      const { data, error } = await supabase.storage
        .from('product-videos')
        .upload(filePath, file);

      if (error) {
        throw error;
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('product-videos')
        .getPublicUrl(filePath);

      setEditProduct(prev => ({ ...prev, video_url: publicUrl }));
      setUploading(false);
      showNotification('Video uploaded successfully!', 'success');
      
    } catch (error) {
      console.error('Video upload error:', error);
      showNotification(error.message || 'Error uploading video. Please try again.', 'error');
      setUploading(false);
    }
  };



  const handleUpdateProduct = async (e) => {
    e.preventDefault();
    
    // Check if at least one price is provided
    const hasPrice = editProduct.price_usd || editProduct.price_gbp || editProduct.price_aed || editProduct.price_sar;
    
    if (!editProduct.name || !hasPrice || !editProduct.stock) {
      showNotification('Please fill in all required fields (Name, at least one currency price, and Stock are mandatory).', 'error');
      return;
    }

    try {
      // Filter out empty features and tags
      const filteredFeatures = (editProduct.features || []).filter(feature => feature && feature.trim() !== '');
      const filteredTags = (editProduct.tags || []).filter(tag => tag && tag.trim() !== '');

      // Helper function to safely parse numeric values
      const parseNumericValue = (value) => {
        if (value === null || value === undefined || value === '' || value === 'undefined' || value === 'null') {
          return null;
        }
        const parsed = parseFloat(value);
        if (isNaN(parsed)) {
          return null;
        }
        return parsed;
      };

      // Helper function to safely parse integer values
      const parseIntValue = (value) => {
        if (value === null || value === undefined || value === '' || value === 'undefined' || value === 'null') {
          return 0;
        }
        const parsed = parseInt(value);
        if (isNaN(parsed)) {
          return 0;
        }
        return parsed;
      };

      // Prepare update data with proper type handling
      const updateData = {
        name: editProduct.name || '',
        price_usd: parseNumericValue(editProduct.price_usd),
        price_gbp: parseNumericValue(editProduct.price_gbp),
        price_aed: parseNumericValue(editProduct.price_aed),
        price_sar: parseNumericValue(editProduct.price_sar),
        original_price_usd: parseNumericValue(editProduct.original_price_usd),
        original_price_gbp: parseNumericValue(editProduct.original_price_gbp),
        original_price_aed: parseNumericValue(editProduct.original_price_aed),
        original_price_sar: parseNumericValue(editProduct.original_price_sar),
        category: editProduct.category || '',
        brand: editProduct.brand || '',
        stock: parseIntValue(editProduct.stock),
        sku: editProduct.sku || '',
        description: editProduct.description || '',
        long_description: editProduct.long_description || '',
        image_url: editProduct.image_url || '',
        gallery_images: editProduct.gallery_images || [],
        video_url: editProduct.video_url || '',
        video_thumbnail: editProduct.video_thumbnail || '',
        specifications: editProduct.specifications || {},
        features: filteredFeatures,
        tags: filteredTags,
        weight: parseNumericValue(editProduct.weight),
        weight_unit: editProduct.weight_unit || 'g',
        dimensions: editProduct.dimensions || '',
        dimension_unit: editProduct.dimension_unit || 'cm',
        countries: editProduct.countries || [],
        color: editProduct.color || '',
        material: editProduct.material || '',
        warranty: editProduct.warranty || '',
        shipping_info: editProduct.shipping_info || '',
        return_policy: editProduct.return_policy || '',
        is_featured: Boolean(editProduct.is_featured),
        is_bestseller: Boolean(editProduct.is_bestseller),
        is_new_arrival: Boolean(editProduct.is_new_arrival),
        meta_title: editProduct.meta_title || '',
        meta_description: editProduct.meta_description || '',
        seo_keywords: editProduct.seo_keywords || '',
        has_variations: Boolean(editProduct.has_variations),
        variations: editProduct.variations || [],
        variationTypes: editProduct.variationTypes || []
      };

      const { error } = await supabase
        .from('products')
        .update(updateData)
        .eq('id', editingProduct.id);

      if (error) {
        throw error;
      }

      // Reset form and go back to products
      setEditingProduct(null);
      setEditProduct({
        name: '',
        price_usd: '',
        price_gbp: '',
        price_aed: '',
        price_sar: '',
        original_price_usd: '',
        original_price_gbp: '',
        original_price_aed: '',
        original_price_sar: '',
        category: '',
        brand: '',
        stock: '',
        sku: '',
        description: '',
        long_description: '',
        image_url: '',
        gallery_images: [],
        video_url: '',
        video_thumbnail: '',
        specifications: {},
        features: [],
        tags: [],
        weight: '',
        weight_unit: 'g',
        dimensions: '',
        dimension_unit: 'cm',
        countries: [],
        color: '',
        material: '',
        warranty: '',
        shipping_info: '',
        return_policy: '',
        is_featured: false,
        is_bestseller: false,
        is_new_arrival: false,
        meta_title: '',
        meta_description: '',
        seo_keywords: '',
        has_variations: false,
        variations: []
      });
      setGalleryImages([]);
      setSpecifications([{ key: '', value: '' }]);
      setFeatures(['']);
      setTags(['']);
      setVariations([]);
      setVariationTypes([]);
      
      setActiveSection('products');
      loadVendorData(); // Reload products
      showNotification('Product updated successfully!', 'success');
      
    } catch (error) {
      console.error('Error updating product:', error);
      showNotification('Error updating product. Please try again.', 'error');
    }
  };

  const handleCancelEdit = () => {
    setEditingProduct(null);
    setEditProduct({
      name: '',
      price_usd: '',
      price_gbp: '',
      price_aed: '',
      price_sar: '',
      original_price_usd: '',
      original_price_gbp: '',
      original_price_aed: '',
      original_price_sar: '',
      category: '',
      brand: '',
      stock: '',
      sku: '',
      description: '',
      long_description: '',
      image_url: '',
      gallery_images: [],
      video_url: '',
      video_thumbnail: '',
      specifications: {},
      features: [],
      tags: [],
      weight: '',
      weight_unit: 'g',
      dimensions: '',
      dimension_unit: 'cm',
      countries: [],
      color: '',
      material: '',
      warranty: '',
      shipping_info: '',
      return_policy: '',
      is_featured: false,
      is_bestseller: false,
      is_new_arrival: false,
      meta_title: '',
      meta_description: '',
      seo_keywords: '',
      has_variations: false,
      variations: [],
      variationTypes: []
    });
    setActiveSection('products');
  };

  const handleGalleryImageUpload = async (files) => {
    try {
      setUploading(true);
      const uploadedUrls = [];
      
      for (const file of files) {
        const fileExt = file.name.split('.').pop();
        const fileName = `${Math.random().toString(36).substring(2)}.${fileExt}`;
        const filePath = `gallery/${fileName}`;

        const { data, error } = await supabase.storage
          .from('product-images')
          .upload(filePath, file);

        if (error) throw error;

        const { data: { publicUrl } } = supabase.storage
          .from('product-images')
          .getPublicUrl(filePath);

        uploadedUrls.push(publicUrl);
      }

      setGalleryImages(prev => [...prev, ...uploadedUrls]);
      setNewProduct(prev => ({ ...prev, gallery_images: [...prev.gallery_images, ...uploadedUrls] }));
      setUploading(false);
      
    } catch (error) {
      console.error('Gallery upload error:', error);
      showNotification('Error uploading gallery images. Please try again.', 'error');
      setUploading(false);
    }
  };

  const removeGalleryImage = (index) => {
    setGalleryImages(prev => prev.filter((_, i) => i !== index));
    setNewProduct(prev => ({
      ...prev,
      gallery_images: prev.gallery_images.filter((_, i) => i !== index)
    }));
  };

  const addSpecification = () => {
    setSpecifications(prev => [...prev, { key: '', value: '' }]);
  };

  const removeSpecification = (index) => {
    setSpecifications(prev => prev.filter((_, i) => i !== index));
  };

  const updateSpecification = (index, field, value) => {
    setSpecifications(prev => prev.map((spec, i) => 
      i === index ? { ...spec, [field]: value } : spec
    ));
  };

  const addFeature = () => {
    setFeatures(prev => [...prev, '']);
  };

  const removeFeature = (index) => {
    setFeatures(prev => prev.filter((_, i) => i !== index));
  };

  const updateFeature = (index, value) => {
    setFeatures(prev => prev.map((feature, i) => i === index ? value : feature));
  };

  const addTag = () => {
    setTags(prev => [...prev, '']);
  };

  const removeTag = (index) => {
    setTags(prev => prev.filter((_, i) => i !== index));
  };

  const updateTag = (index, value) => {
    setTags(prev => prev.map((tag, i) => i === index ? value : tag));
  };

  // Variation helper functions
  const addVariationType = () => {
    setVariationTypes([...variationTypes, { name: '', options: [''] }]);
  };

  const removeVariationType = (index) => {
    const newTypes = variationTypes.filter((_, i) => i !== index);
    setVariationTypes(newTypes);
    generateVariations(newTypes);
  };

  const updateVariationType = (index, field, value) => {
    const newTypes = [...variationTypes];
    if (field === 'name') {
      newTypes[index].name = value;
    }
    setVariationTypes(newTypes);
    generateVariations(newTypes);
  };

  const addVariationOption = (typeIndex) => {
    const newTypes = [...variationTypes];
    newTypes[typeIndex].options.push('');
    setVariationTypes(newTypes);
    generateVariations(newTypes);
  };

  const removeVariationOption = (typeIndex, optionIndex) => {
    const newTypes = [...variationTypes];
    newTypes[typeIndex].options.splice(optionIndex, 1);
    setVariationTypes(newTypes);
    generateVariations(newTypes);
  };

  const updateVariationOption = (typeIndex, optionIndex, value) => {
    const newTypes = [...variationTypes];
    newTypes[typeIndex].options[optionIndex] = value;
    setVariationTypes(newTypes);
    generateVariations(newTypes);
  };

  const generateVariations = (types) => {
    if (types.length === 0 || types.some(type => type.options.length === 0)) {
      setVariations([]);
      return;
    }

    // Generate all possible combinations
    const generateCombinations = (arrays) => {
      if (arrays.length === 0) return [[]];
      const [first, ...rest] = arrays;
      const restCombinations = generateCombinations(rest);
      return first.flatMap(item => 
        restCombinations.map(combination => [item, ...combination])
      );
    };

    const optionArrays = types.map(type => type.options);
    const combinations = generateCombinations(optionArrays);
    
    const newVariations = combinations.map((combination, index) => {
      const variationName = combination.join(' / ');
      return {
        id: `var_${index}`,
        name: variationName,
        attributes: types.map((type, i) => ({
          type: type.name,
          value: combination[i]
        })),
        price: '',
        original_price: '',
        stock: '',
        sku: '',
        image_url: '',
        weight: '',
        is_active: true
      };
    });

    setVariations(newVariations);
  };

  const updateVariation = (index, field, value) => {
    const newVariations = [...variations];
    newVariations[index] = { ...newVariations[index], [field]: value };
    setVariations(newVariations);
  };

  const handleVariationImageUpload = async (file, variationIndex) => {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}_${Math.random().toString(36).substring(2)}.${fileExt}`;
      const filePath = `product-variations/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('product-images')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('product-images')
        .getPublicUrl(filePath);

      updateVariation(variationIndex, 'image_url', publicUrl);
    } catch (error) {
      console.error('Error uploading variation image:', error);
      showNotification('Error uploading variation image', 'error');
    }
  };

  // Edit form helper functions
  const handleEditGalleryImageUpload = async (files) => {
    try {
      setUploading(true);
      const uploadedUrls = [];
      
      for (const file of files) {
        const fileExt = file.name.split('.').pop();
        const fileName = `${Math.random().toString(36).substring(2)}.${fileExt}`;
        const filePath = `gallery/${fileName}`;

        const { data, error } = await supabase.storage
          .from('product-images')
          .upload(filePath, file);

        if (error) throw error;

        const { data: { publicUrl } } = supabase.storage
          .from('product-images')
          .getPublicUrl(filePath);

        uploadedUrls.push(publicUrl);
      }

      setEditProduct(prev => ({ 
        ...prev, 
        gallery_images: [...(prev.gallery_images || []), ...uploadedUrls] 
      }));
      setUploading(false);
      
    } catch (error) {
      console.error('Edit gallery upload error:', error);
      showNotification('Error uploading gallery images. Please try again.', 'error');
      setUploading(false);
    }
  };

  const removeEditGalleryImage = (index) => {
    setEditProduct(prev => ({
      ...prev,
      gallery_images: prev.gallery_images.filter((_, i) => i !== index)
    }));
  };

  const addEditSpecification = () => {
    setEditProduct(prev => ({
      ...prev,
      specifications: { ...prev.specifications, [`spec_${Date.now()}`]: '' }
    }));
  };

  const removeEditSpecification = (index) => {
    const keys = Object.keys(editProduct.specifications || {});
    const keyToRemove = keys[index];
    setEditProduct(prev => {
      const newSpecs = { ...prev.specifications };
      delete newSpecs[keyToRemove];
      return { ...prev, specifications: newSpecs };
    });
  };

  const updateEditSpecification = (index, field, value) => {
    const keys = Object.keys(editProduct.specifications || {});
    const key = keys[index];
    setEditProduct(prev => ({
      ...prev,
      specifications: { ...prev.specifications, [key]: value }
    }));
  };

  const addEditFeature = () => {
    setEditProduct(prev => ({
      ...prev,
      features: [...(prev.features || []), '']
    }));
  };

  const removeEditFeature = (index) => {
    setEditProduct(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  const updateEditFeature = (index, value) => {
    setEditProduct(prev => ({
      ...prev,
      features: prev.features.map((feature, i) => i === index ? value : feature)
    }));
  };

  const addEditTag = () => {
    setEditProduct(prev => ({
      ...prev,
      tags: [...(prev.tags || []), '']
    }));
  };

  const removeEditTag = (index) => {
    setEditProduct(prev => ({
      ...prev,
      tags: prev.tags.filter((_, i) => i !== index)
    }));
  };

  const updateEditTag = (index, value) => {
    setEditProduct(prev => ({
      ...prev,
      tags: prev.tags.map((tag, i) => i === index ? value : tag)
    }));
  };

  // Edit Variation Functions
  const addEditVariationType = () => {
    const newVariationTypes = [...(editProduct.variationTypes || []), { name: '', options: [''] }];
    setEditProduct(prev => ({ ...prev, variationTypes: newVariationTypes }));
  };

  const removeEditVariationType = (index) => {
    const newVariationTypes = editProduct.variationTypes.filter((_, i) => i !== index);
    setEditProduct(prev => ({ ...prev, variationTypes: newVariationTypes }));
    generateEditVariations(newVariationTypes);
  };

  const updateEditVariationType = (index, field, value) => {
    const newTypes = [...(editProduct.variationTypes || [])];
    if (field === 'name') {
      newTypes[index].name = value;
    }
    setEditProduct(prev => ({ ...prev, variationTypes: newTypes }));
    generateEditVariations(newTypes);
  };

  const addEditVariationOption = (typeIndex) => {
    const newTypes = [...(editProduct.variationTypes || [])];
    newTypes[typeIndex].options.push('');
    setEditProduct(prev => ({ ...prev, variationTypes: newTypes }));
    generateEditVariations(newTypes);
  };

  const removeEditVariationOption = (typeIndex, optionIndex) => {
    const newTypes = [...(editProduct.variationTypes || [])];
    newTypes[typeIndex].options.splice(optionIndex, 1);
    setEditProduct(prev => ({ ...prev, variationTypes: newTypes }));
    generateEditVariations(newTypes);
  };

  const updateEditVariationOption = (typeIndex, optionIndex, value) => {
    const newTypes = [...(editProduct.variationTypes || [])];
    newTypes[typeIndex].options[optionIndex] = value;
    setEditProduct(prev => ({ ...prev, variationTypes: newTypes }));
    generateEditVariations(newTypes);
  };

  const generateEditVariations = (types) => {
    if (!types || types.length === 0) {
      setEditProduct(prev => ({ ...prev, variations: [] }));
      return;
    }

    const generateCombinations = (arrays) => {
      if (arrays.length === 0) return [[]];
      const [first, ...rest] = arrays;
      const restCombinations = generateCombinations(rest);
      return first.flatMap(item => restCombinations.map(combo => [item, ...combo]));
    };

    const optionArrays = types.map(type => type.options.filter(opt => opt.trim()));
    const combinations = generateCombinations(optionArrays);
    
    const variations = combinations.map(combo => {
      const combination = combo.join(' - ');
      const existingVariation = editProduct.variations?.find(v => v.combination === combination);
      
      return {
        combination,
        price: existingVariation?.price || '',
        stock: existingVariation?.stock || '',
        sku: existingVariation?.sku || '',
        weight: existingVariation?.weight || '',
        image_url: existingVariation?.image_url || ''
      };
    });

    setEditProduct(prev => ({ ...prev, variations }));
  };

  const updateEditVariation = (index, field, value) => {
    const newVariations = [...(editProduct.variations || [])];
    newVariations[index] = { ...newVariations[index], [field]: value };
    setEditProduct(prev => ({ ...prev, variations: newVariations }));
  };

  const handleEditVariationImageUpload = async (file, variationIndex) => {
    try {
      setUploading(true);
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random()}.${fileExt}`;
      const filePath = `variations/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('product-images')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('product-images')
        .getPublicUrl(filePath);

      updateEditVariation(variationIndex, 'image_url', publicUrl);
    } catch (error) {
      console.error('Error uploading variation image:', error);
      showNotification('Error uploading variation image', 'error');
    } finally {
      setUploading(false);
    }
  };

  const renderNotification = () => {
    if (!notification) return null;

    const isSuccess = notification.type === 'success';
    const isError = notification.type === 'error';

    return (
      <div className={`notification ${notification.type}`}>
        <div className="notification-content">
          <div className="notification-icon">
            {isSuccess ? '✅' : isError ? '❌' : 'ℹ️'}
          </div>
          <div className="notification-message">
            {notification.message}
          </div>
          <button 
            className="notification-close"
            onClick={() => setNotification(null)}
          >
            ×
          </button>
        </div>
        <div className="notification-progress"></div>
      </div>
    );
  };

  const renderViewModal = () => {
    if (!showViewModal || !viewingProduct) return null;

    const { symbol, price, originalPrice } = getPrimaryCurrencyAndPrice(viewingProduct);

    return (
      <div className="modal-overlay" onClick={closeViewModal}>
        <div className="modal-content product-view-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Product Details</h2>
            <button className="modal-close" onClick={closeViewModal}>×</button>
          </div>
          <div className="modal-body">
            <div className="product-view-content">
              {/* Product Image */}
              <div className="product-view-image">
                {viewingProduct.image_url ? (
                  <img 
                    src={viewingProduct.image_url} 
                    alt={viewingProduct.name}
                    className="product-main-image"
                  />
                ) : (
                  <div className="product-placeholder">
                    <span>📦</span>
                  </div>
                )}
              </div>

              {/* Product Information */}
              <div className="product-view-info">
                <h3 className="product-name">{viewingProduct.name}</h3>
                
                {/* Pricing */}
                <div className="product-pricing">
                  <span className="current-price">{symbol}{price.toFixed(2)}</span>
                  {originalPrice && originalPrice > price && (
                    <span className="original-price">{symbol}{originalPrice.toFixed(2)}</span>
                  )}
                </div>

                {/* Basic Details */}
                <div className="product-details-grid">
                  <div className="detail-item">
                    <span className="detail-label">Category:</span>
                    <span className="detail-value">{viewingProduct.category || 'Uncategorized'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Stock:</span>
                    <span className={`detail-value ${viewingProduct.stock < 10 ? 'low-stock' : ''}`}>
                      {viewingProduct.stock || 0} units
                    </span>
                  </div>
                  {viewingProduct.brand && (
                    <div className="detail-item">
                      <span className="detail-label">Brand:</span>
                      <span className="detail-value">{viewingProduct.brand}</span>
                    </div>
                  )}
                  {viewingProduct.sku && (
                    <div className="detail-item">
                      <span className="detail-label">SKU:</span>
                      <span className="detail-value">{viewingProduct.sku}</span>
                    </div>
                  )}
                  {viewingProduct.weight && (
                    <div className="detail-item">
                      <span className="detail-label">Weight:</span>
                      <span className="detail-value">{viewingProduct.weight} {viewingProduct.weight_unit || 'g'}</span>
                    </div>
                  )}
                  {viewingProduct.dimensions && (
                    <div className="detail-item">
                      <span className="detail-label">Dimensions:</span>
                      <span className="detail-value">{viewingProduct.dimensions} {viewingProduct.dimension_unit || 'cm'}</span>
                    </div>
                  )}
                  <div className="detail-item">
                    <span className="detail-label">Status:</span>
                    <span className={`detail-value status-${viewingProduct.status || 'active'}`}>
                      {viewingProduct.status || 'Active'}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Created:</span>
                    <span className="detail-value">{new Date(viewingProduct.created_at).toLocaleDateString()}</span>
                  </div>
                </div>

                {/* Description */}
                {viewingProduct.description && (
                  <div className="product-description">
                    <h4>Description</h4>
                    <p>{viewingProduct.description}</p>
                  </div>
                )}

                {/* Long Description */}
                {viewingProduct.long_description && (
                  <div className="product-long-description">
                    <h4>Detailed Description</h4>
                    <p>{viewingProduct.long_description}</p>
                  </div>
                )}

                {/* Specifications */}
                {viewingProduct.specifications && Object.keys(viewingProduct.specifications).length > 0 && (
                  <div className="product-specifications">
                    <h4>Specifications</h4>
                    <div className="specs-grid">
                      {Object.entries(viewingProduct.specifications).map(([key, value]) => (
                        <div key={key} className="spec-item">
                          <span className="spec-key">{key}:</span>
                          <span className="spec-value">{value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Features */}
                {viewingProduct.features && viewingProduct.features.length > 0 && (
                  <div className="product-features">
                    <h4>Features</h4>
                    <ul className="features-list">
                      {viewingProduct.features.map((feature, index) => (
                        <li key={index}>{feature}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Tags */}
                {viewingProduct.tags && viewingProduct.tags.length > 0 && (
                  <div className="product-tags">
                    <h4>Tags</h4>
                    <div className="tags-container">
                      {viewingProduct.tags.map((tag, index) => (
                        <span key={index} className="tag">{tag}</span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Gallery Images */}
                {viewingProduct.gallery_images && viewingProduct.gallery_images.length > 0 && (
                  <div className="product-gallery">
                    <h4>Gallery Images</h4>
                    <div className="gallery-grid">
                      {viewingProduct.gallery_images.map((image, index) => (
                        <div key={index} className="gallery-item">
                          <img src={image} alt={`${viewingProduct.name} - Image ${index + 1}`} />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Video */}
                {viewingProduct.video_url && (
                  <div className="product-video">
                    <h4>Product Video</h4>
                    <video controls className="product-video-player">
                      <source src={viewingProduct.video_url} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  </div>
                )}

                {/* Additional Information */}
                <div className="additional-info">
                  {viewingProduct.warranty && (
                    <div className="info-item">
                      <h4>Warranty</h4>
                      <p>{viewingProduct.warranty}</p>
                    </div>
                  )}
                  {viewingProduct.shipping_info && (
                    <div className="info-item">
                      <h4>Shipping Information</h4>
                      <p>{viewingProduct.shipping_info}</p>
                    </div>
                  )}
                  {viewingProduct.return_policy && (
                    <div className="info-item">
                      <h4>Return Policy</h4>
                      <p>{viewingProduct.return_policy}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button className="modal-btn secondary" onClick={closeViewModal}>
              Close
            </button>
            <button 
              className="modal-btn primary" 
              onClick={() => {
                closeViewModal();
                handleEditProduct(viewingProduct);
              }}
            >
              Edit Product
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderOverview = () => (
    <div className="dashboard-overview">
      <div className="dashboard-stats">
        <div className="stat-card">
          <div className="stat-icon">🛒</div>
          <div className="stat-info">
            <h3>Total Orders</h3>
            <p className="stat-number">{orders.length}</p>
            <p className="stat-label">All time orders</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">💰</div>
          <div className="stat-info">
            <h3>Total Revenue</h3>
                            <p className="stat-number">£{orders.reduce((sum, order) => sum + order.total, 0).toFixed(2)}</p>
            <p className="stat-label">All time revenue</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">⭐</div>
          <div className="stat-info">
            <h3>Rating</h3>
            <p className="stat-number">4.8</p>
            <p className="stat-label">Average rating</p>
          </div>
        </div>
      </div>

      <div className="dashboard-sections">
        <div className="dashboard-section">
          <h3>Quick Actions</h3>
          <div className="action-buttons">
            <button className="action-btn" onClick={() => setActiveSection('products')}>
              <span className="action-icon">➕</span>
              Add New Product
            </button>
            <button className="action-btn" onClick={() => setActiveSection('orders')}>
              <span className="action-icon">📋</span>
              View Orders
            </button>
            <button className="action-btn" onClick={() => setActiveSection('reports')}>
              <span className="action-icon">📊</span>
              View Reports
            </button>
            <button className="action-btn">
              <span className="action-icon">⚙️</span>
              Settings
            </button>
          </div>
        </div>

        <div className="dashboard-section">
          <h3>Recent Activity</h3>
          <div className="activity-list">
            <div className="activity-item">
              <div className="activity-icon">🎉</div>
              <div className="activity-content">
                <p>Welcome to your vendor dashboard!</p>
                <span className="activity-time">Just now</span>
              </div>
            </div>
            <div className="activity-item">
              <div className="activity-icon">📝</div>
              <div className="activity-content">
                <p>Account created successfully</p>
                <span className="activity-time">Today</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderProducts = () => (
    <div className="dashboard-products">
      <div className="section-header">
        <div className="header-content">
          <h2>My Products</h2>
          <p className="header-subtitle">Manage your product catalog</p>
        </div>
        <button className="add-product-btn" onClick={() => setActiveSection('add-product')}>
          <span className="btn-icon">➕</span>
          <span className="btn-text">Add New Product</span>
        </button>
      </div>
      
      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading your products...</p>
        </div>
      ) : products.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📦</div>
          <h3>No Products Yet</h3>
          <p>Start building your catalog by adding your first product</p>
          <button className="empty-cta-btn" onClick={() => setActiveSection('add-product')}>
            Add Your First Product
          </button>
        </div>
      ) : (
        <div className="products-container">
          <div className="products-stats">
                        <div className="stat-card">
              <div className="stat-icon">📦</div>
              <div className="stat-content">
                <div className="stat-number">{products.length}</div>
                <div className="stat-label">Total Products</div>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">📊</div>
              <div className="stat-content">
                <div className="stat-number">{products.filter(p => p.stock < 10).length}</div>
                <div className="stat-label">Low Stock</div>
              </div>
            </div>
          </div>
          
          <div className="products-grid">
            {products.map(product => (
              <div key={product.id} className="product-card-new">
                <div className="product-image-section">
                  {product.image_url ? (
                    <img 
                      src={product.image_url} 
                      alt={product.name}
                      className="product-main-image"
                    />
                  ) : (
                    <div className="product-placeholder-new">
                      <span className="placeholder-icon">📦</span>
                    </div>
                  )}
                  <div className="product-badges">
                    <div className={`status-badge ${product.status}`}>
                      <span className="status-dot"></span>
                      {product.status}
                    </div>
                    {product.is_featured && <div className="featured-badge">Featured</div>}
                    {product.is_bestseller && <div className="bestseller-badge">Best Seller</div>}
                    {product.is_new_arrival && <div className="new-badge">New</div>}
                  </div>
                  <div className="product-actions-overlay">
                    <button 
                      className="quick-action-btn edit-action" 
                      title="Edit Product"
                      onClick={() => handleEditProduct(product)}
                    >
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                      </svg>
                    </button>
                    <button 
                      className="quick-action-btn view-action" 
                      title="View Details"
                      onClick={() => handleViewProduct(product)}
                    >
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                      </svg>
                    </button>
                    <button 
                      className="quick-action-btn delete-action" 
                      title="Delete Product"
                      onClick={() => handleDeleteProduct(product)}
                    >
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                      </svg>
                    </button>
                  </div>
                </div>
                
                <div className="product-content-new">
                  <div className="product-header-new">
                    <h3 className="product-title-new">{product.name}</h3>
                    <div className="product-price-new">
                      {(() => {
                        const { symbol, price, originalPrice } = getPrimaryCurrencyAndPrice(product);
                        return (
                          <>
                            <span className="current-price">{symbol}{price.toFixed(2)}</span>
                            {originalPrice && originalPrice > price && (
                              <span className="original-price">{symbol}{originalPrice.toFixed(2)}</span>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                  
                  <div className="product-details-new">
                    <div className="detail-row">
                      <div className="detail-item-new">
                        <span className="detail-icon">🏷️</span>
                        <span className="detail-label-new">Category</span>
                        <span className="detail-value-new">{product.category || 'Uncategorized'}</span>
                      </div>
                      <div className="detail-item-new">
                        <span className="detail-icon">📦</span>
                        <span className="detail-label-new">Stock</span>
                        <span className={`detail-value-new ${product.stock < 10 ? 'low-stock' : ''}`}>
                          {product.stock} units
                        </span>
                      </div>
                    </div>
                    
                    {product.brand && (
                      <div className="detail-item-new">
                        <span className="detail-icon">🏢</span>
                        <span className="detail-label-new">Brand</span>
                        <span className="detail-value-new">{product.brand}</span>
                      </div>
                    )}
                    
                    {product.sku && (
                      <div className="detail-item-new">
                        <span className="detail-icon">🔢</span>
                        <span className="detail-label-new">SKU</span>
                        <span className="detail-value-new">{product.sku}</span>
                      </div>
                    )}
                    
                    {product.dimensions && (
                      <div className="detail-item-new">
                        <span className="detail-icon">📏</span>
                        <span className="detail-label-new">Dimensions</span>
                        <span className="detail-value-new">{product.dimensions} {product.dimension_unit || 'cm'}</span>
                      </div>
                    )}
                    
                    {product.description && (
                      <div className="product-description-new">
                        <p>{product.description.length > 80 
                          ? `${product.description.substring(0, 80)}...` 
                          : product.description
                        }</p>
                      </div>
                    )}
                  </div>
                  
                  <div className="product-footer-new">
                    <div className="product-meta">
                      <span className="meta-item">
                        <span className="meta-icon">📅</span>
                        {new Date(product.created_at).toLocaleDateString()}
                      </span>
                      {product.gallery_images && product.gallery_images.length > 0 && (
                        <span className="meta-item">
                          <span className="meta-icon">🖼️</span>
                          {product.gallery_images.length} images
                        </span>
                      )}
                    </div>
                    
                    <div className="product-actions-new">
                      <button 
                        className="action-btn-new edit-btn-new" 
                        onClick={() => handleEditProduct(product)}
                      >
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                        </svg>
                        Edit
                      </button>
                      <button 
                        className="action-btn-new view-btn-new" 
                        onClick={() => handleViewProduct(product)}
                      >
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                        </svg>
                        View
                      </button>
                      <button 
                        className="action-btn-new delete-btn-new" 
                        onClick={() => handleDeleteProduct(product)}
                      >
                        <svg viewBox="0 0 24 24" fill="currentColor">
                          <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                        </svg>
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderAddProduct = () => (
    <div className="dashboard-add-product">
      <div className="section-header">
        <h2>{editingProduct ? 'Edit Product' : 'Add New Product'}</h2>
        <button className="back-btn" onClick={() => {
          setActiveSection('products');
          setEditingProduct(null);
          // Reset form data
          setNewProduct({
            name: '',
            price_usd: '',
            price_gbp: '',
            price_aed: '',
            price_sar: '',
            original_price_usd: '',
            original_price_gbp: '',
            original_price_aed: '',
            original_price_sar: '',
            category: '',
            subcategories: [],
            brand: '',
            stock: '',
            sku: '',
            description: '',
            long_description: '',
            image_url: '',
            gallery_images: [],
            video_url: '',
            video_thumbnail: '',
            specifications: {},
            features: [],
            tags: [],
            weight: '',
            weight_unit: 'g',
            dimensions: '',
            dimension_unit: 'cm',
            countries: [],
            color: '',
            material: '',
            warranty: '',
            shipping_info: '',
            return_policy: '',
            is_featured: false,
            is_bestseller: false,
            is_new_arrival: false,
            meta_title: '',
            meta_description: '',
            seo_keywords: '',
            has_variations: false,
            variations: []
          });
          setGalleryImages([]);
          setSpecifications([{ key: '', value: '' }]);
          setFeatures(['']);
          setTags(['']);
          setVariations([]);
          setVariationTypes([]);
        }}>
          ← Back to Products
        </button>
      </div>


      
      <div className="add-product-form">
        <form onSubmit={handleAddProduct}>
          {/* Basic Information */}
          <div className="form-section">
            <h3>Basic Information</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Available Countries</label>
                <div className="countries-section">
                  <div className="country-item">
                    <input 
                      type="checkbox" 
                      id="country-uae"
                      checked={newProduct.countries && newProduct.countries.includes('UAE')}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setNewProduct(prev => ({ 
                            ...prev, 
                            countries: [...prev.countries, 'UAE'] 
                          }));
                        } else {
                          setNewProduct(prev => ({ 
                            ...prev, 
                            countries: prev.countries.filter(country => country !== 'UAE') 
                          }));
                        }
                      }}
                    />
                    <label htmlFor="country-uae">UAE</label>
                  </div>
                  <div className="country-item">
                    <input 
                      type="checkbox" 
                      id="country-usa"
                      checked={newProduct.countries && newProduct.countries.includes('USA')}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setNewProduct(prev => ({ 
                            ...prev, 
                            countries: [...prev.countries, 'USA'] 
                          }));
                        } else {
                          setNewProduct(prev => ({ 
                            ...prev, 
                            countries: prev.countries.filter(country => country !== 'USA') 
                          }));
                        }
                      }}
                    />
                    <label htmlFor="country-usa">USA</label>
                  </div>
                  <div className="country-item">
                    <input 
                      type="checkbox" 
                      id="country-uk"
                      checked={newProduct.countries && newProduct.countries.includes('UK')}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setNewProduct(prev => ({ 
                            ...prev, 
                            countries: [...prev.countries, 'UK'] 
                          }));
                        } else {
                          setNewProduct(prev => ({ 
                            ...prev, 
                            countries: prev.countries.filter(country => country !== 'UK') 
                          }));
                        }
                      }}
                    />
                    <label htmlFor="country-uk">UK</label>
                  </div>
                  <div className="country-item">
                    <input 
                      type="checkbox" 
                      id="country-saudi"
                      checked={newProduct.countries && newProduct.countries.includes('Saudi Arabia')}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setNewProduct(prev => ({ 
                            ...prev, 
                            countries: [...prev.countries, 'Saudi Arabia'] 
                          }));
                        } else {
                          setNewProduct(prev => ({ 
                            ...prev, 
                            countries: prev.countries.filter(country => country !== 'Saudi Arabia') 
                          }));
                        }
                      }}
                    />
                    <label htmlFor="country-saudi">Saudi Arabia</label>
                  </div>
                </div>
              </div>
              <div className="form-group">
                <label>Product Name *</label>
                <input 
                  type="text" 
                  placeholder="Enter product name"
                  value={newProduct.name}
                  onChange={(e) => setNewProduct(prev => ({ ...prev, name: e.target.value }))}
                  required
                />
              </div>
              <div className="form-group">
                <label>Brand</label>
                <input 
                  type="text" 
                  placeholder="Enter brand name"
                  value={newProduct.brand}
                  onChange={(e) => setNewProduct(prev => ({ ...prev, brand: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label>SKU</label>
                <input 
                  type="text" 
                  placeholder="Stock Keeping Unit"
                  value={newProduct.sku}
                  onChange={(e) => setNewProduct(prev => ({ ...prev, sku: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label>Category</label>
                <select 
                  value={newProduct.category}
                  onChange={(e) => setNewProduct(prev => ({ ...prev, category: e.target.value, subcategory: '' }))}
                  required
                  disabled={loading}
                >
                  <option value="">
                    {loading ? 'Loading categories...' : 'Select a category'}
                  </option>
                  {categories && categories.length > 0 && categories.map(category => (
                    <option key={category.id} value={category.name}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Subcategories</label>
                <div className="subcategories-container">
                  <div className="subcategories-list">
                    {newProduct.subcategories && newProduct.subcategories.length > 0 && newProduct.subcategories.map((subcategory, index) => (
                      <div key={index} className="subcategory-tag">
                        <span>{subcategory}</span>
                        <button
                          type="button"
                          className="remove-subcategory"
                          onClick={() => {
                            const newSubcategories = newProduct.subcategories.filter((_, i) => i !== index);
                            setNewProduct(prev => ({ ...prev, subcategories: newSubcategories }));
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="subcategory-input-section">
                    <input
                      type="text"
                      placeholder="Type a subcategory name..."
                      value={newProduct.newSubcategory || ''}
                      onChange={(e) => setNewProduct(prev => ({ 
                        ...prev, 
                        newSubcategory: e.target.value 
                      }))}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && newProduct.newSubcategory?.trim()) {
                          e.preventDefault();
                          if (!newProduct.subcategories.includes(newProduct.newSubcategory.trim())) {
                            setNewProduct(prev => ({ 
                              ...prev, 
                              subcategories: [...prev.subcategories, prev.newSubcategory.trim()],
                              newSubcategory: ''
                            }));
                          }
                        }
                      }}
                      disabled={loading}
                    />
                    <button
                      type="button"
                      className="add-subcategory-btn"
                      onClick={() => {
                        if (newProduct.newSubcategory?.trim() && 
                            !newProduct.subcategories.includes(newProduct.newSubcategory.trim())) {
                          setNewProduct(prev => ({ 
                            ...prev, 
                            subcategories: [...prev.subcategories, prev.newSubcategory.trim()],
                            newSubcategory: ''
                          }));
                        }
                      }}
                      disabled={!newProduct.newSubcategory?.trim() || loading}
                    >
                      +
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Pricing */}
          <div className="form-section">
            <h3>Pricing & Inventory</h3>
            <div className="form-grid">
              {/* USD Prices - Show only if USA is selected */}
              {newProduct.countries && newProduct.countries.includes('USA') && (
                <>
                  <div className="form-group">
                    <label>Current Price (USD) *</label>
                    <input 
                      type="number" 
                      step="0.01" 
                      placeholder="0.00"
                      value={newProduct.price_usd}
                      onChange={(e) => setNewProduct(prev => ({ ...prev, price_usd: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Original Price (USD)</label>
                    <input 
                      type="number" 
                      step="0.01" 
                      placeholder="0.00"
                      value={newProduct.original_price_usd}
                      onChange={(e) => setNewProduct(prev => ({ ...prev, original_price_usd: e.target.value }))}
                    />
                  </div>
                </>
              )}
              
              {/* GBP Prices - Show only if UK is selected */}
              {newProduct.countries && newProduct.countries.includes('UK') && (
                <>
                  <div className="form-group">
                    <label>Current Price (GBP) *</label>
                    <input 
                      type="number" 
                      step="0.01" 
                      placeholder="0.00"
                      value={newProduct.price_gbp}
                      onChange={(e) => setNewProduct(prev => ({ ...prev, price_gbp: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Original Price (GBP)</label>
                    <input 
                      type="number" 
                      step="0.01" 
                      placeholder="0.00"
                      value={newProduct.original_price_gbp}
                      onChange={(e) => setNewProduct(prev => ({ ...prev, original_price_gbp: e.target.value }))}
                    />
                  </div>
                </>
              )}
              
              {/* AED Prices - Show only if UAE is selected */}
              {newProduct.countries && newProduct.countries.includes('UAE') && (
                <>
                  <div className="form-group">
                    <label>Current Price (AED) *</label>
                    <input 
                      type="number" 
                      step="0.01" 
                      placeholder="0.00"
                      value={newProduct.price_aed}
                      onChange={(e) => setNewProduct(prev => ({ ...prev, price_aed: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Original Price (AED)</label>
                    <input 
                      type="number" 
                      step="0.01" 
                      placeholder="0.00"
                      value={newProduct.original_price_aed}
                      onChange={(e) => setNewProduct(prev => ({ ...prev, original_price_aed: e.target.value }))}
                    />
                  </div>
                </>
              )}
              
              {/* KSA Prices - Show only if Saudi Arabia is selected */}
              {newProduct.countries && newProduct.countries.includes('Saudi Arabia') && (
                <>
                  <div className="form-group">
                    <label>Current Price (KSA) *</label>
                    <input 
                      type="number" 
                      step="0.01" 
                      placeholder="0.00"
                      value={newProduct.price_sar}
                      onChange={(e) => setNewProduct(prev => ({ ...prev, price_sar: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Original Price (KSA)</label>
                    <input 
                      type="number" 
                      step="0.01" 
                      placeholder="0.00"
                      value={newProduct.original_price_sar}
                      onChange={(e) => setNewProduct(prev => ({ ...prev, original_price_sar: e.target.value }))}
                    />
                  </div>
                </>
              )}
              
              {/* Stock */}
              <div className="form-group">
                <label>Stock Quantity *</label>
                <input 
                  type="number" 
                  placeholder="0"
                  value={newProduct.stock}
                  onChange={(e) => setNewProduct(prev => ({ ...prev, stock: e.target.value }))}
                  required
                />
              </div>
            </div>
          </div>

          {/* Descriptions */}
          <div className="form-section">
            <h3>Product Descriptions</h3>
            <div className="form-group">
              <label>Short Description</label>
              <textarea 
                rows="3" 
                placeholder="Brief product description for listings"
                value={newProduct.description}
                onChange={(e) => setNewProduct(prev => ({ ...prev, description: e.target.value }))}
              ></textarea>
            </div>
            <div className="form-group">
              <label>Detailed Description</label>
              <textarea 
                rows="6" 
                placeholder="Comprehensive product description for detail page"
                value={newProduct.long_description}
                onChange={(e) => setNewProduct(prev => ({ ...prev, long_description: e.target.value }))}
              ></textarea>
            </div>
          </div>

          {/* Images */}
          <div className="form-section">
            <h3>Product Images</h3>
            <div className="form-group">
              <label>Main Product Image</label>
              <div className="image-upload-section">
                <input 
                  type="file" 
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      handleImageUpload(file);
                    }
                  }}
                />
                {uploading && <p className="upload-status">Uploading image...</p>}
                {newProduct.image_url && (
                  <div className="image-preview">
                    <img 
                      src={newProduct.image_url} 
                      alt="Main product" 
                    />
                  </div>
                )}
              </div>
            </div>
            
            <div className="form-group">
              <label>Gallery Images</label>
              <div className="gallery-upload-section">
                <div className="gallery-upload-controls">
                  <input 
                    type="file" 
                    accept="image/*"
                    multiple
                    id="gallery-upload"
                    onChange={(e) => {
                      const files = Array.from(e.target.files);
                      if (files.length > 0) {
                        handleGalleryImageUpload(files);
                      }
                    }}
                    style={{ display: 'none' }}
                  />
                  <label htmlFor="gallery-upload" className="upload-btn">
                    📷 Add Images
                  </label>
                  <button 
                    type="button" 
                    className="add-more-images-btn"
                    onClick={() => document.getElementById('gallery-upload').click()}
                  >
                    ➕ Add More Images
                  </button>
                </div>
                {uploading && <p className="upload-status">Uploading gallery images...</p>}
                <div className="gallery-preview">
                  {galleryImages.map((image, index) => (
                    <div key={index} className="gallery-item">
                      <img src={image} alt={`Gallery ${index + 1}`} />
                      <button 
                        type="button" 
                        className="remove-gallery-btn"
                        onClick={() => removeGalleryImage(index)}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  {galleryImages.length === 0 && (
                    <div className="gallery-empty">
                      <span>📷</span>
                      <p>No gallery images added yet</p>
                      <small>Click "Add Images" to upload product photos</small>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Videos */}
          <div className="form-section">
            <h3>Product Videos</h3>
            <div className="form-group">
              <label>Product Video</label>
              <div className="video-upload-section">
                <input 
                  type="file" 
                  accept="video/*"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      handleVideoUpload(file);
                    }
                  }}
                />
                {uploading && <p className="upload-status">Uploading video...</p>}
                {newProduct.video_url && (
                  <div className="video-preview">
                    <video 
                      controls 
                      width="100%" 
                      height="200"
                      src={newProduct.video_url}
                    >
                      Your browser does not support the video tag.
                    </video>
                    <button 
                      type="button" 
                      className="remove-video-btn"
                      onClick={() => setNewProduct(prev => ({ ...prev, video_url: '' }))}
                    >
                      Remove Video
                    </button>
                  </div>
                )}
                <div className="video-info">
                  <p><strong>Supported formats:</strong> MP4, WebM, OGG, MOV</p>
                  <p><strong>Maximum size:</strong> 100MB</p>
                  <p><strong>Recommended:</strong> 720p or higher resolution</p>
                </div>
              </div>
            </div>
            

          </div>

          {/* Product Details */}
          <div className="form-section">
            <h3>Product Details</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Weight</label>
                <div className="weight-input-group">
                  <input 
                    type="number" 
                    step="0.01" 
                    placeholder="0.00"
                    value={newProduct.weight}
                    onChange={(e) => setNewProduct(prev => ({ ...prev, weight: e.target.value }))}
                  />
                  <select 
                    value={newProduct.weight_unit}
                    onChange={(e) => setNewProduct(prev => ({ ...prev, weight_unit: e.target.value }))}
                  >
                    <option value="g">Grams (g)</option>
                    <option value="kg">Kilograms (kg)</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>Dimensions (L×W×H)</label>
                <div className="dimensions-input-group">
                  <input 
                    type="text" 
                    placeholder="e.g., 10×5×2"
                    value={newProduct.dimensions}
                    onChange={(e) => setNewProduct(prev => ({ ...prev, dimensions: e.target.value }))}
                  />
                  <select
                    value={newProduct.dimension_unit || 'cm'}
                    onChange={(e) => setNewProduct(prev => ({ ...prev, dimension_unit: e.target.value }))}
                  >
                    <option value="cm">Centimeters (cm)</option>
                    <option value="mm">Millimeters (mm)</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>Color</label>
                <input 
                  type="text" 
                  placeholder="e.g., Red, Blue, Black"
                  value={newProduct.color}
                  onChange={(e) => setNewProduct(prev => ({ ...prev, color: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label>Material</label>
                <input 
                  type="text" 
                  placeholder="e.g., Cotton, Plastic, Metal"
                  value={newProduct.material}
                  onChange={(e) => setNewProduct(prev => ({ ...prev, material: e.target.value }))}
                />
              </div>

            </div>
          </div>

          {/* Specifications */}
          <div className="form-section">
            <h3>Specifications</h3>
            <div className="specifications-section">
              {specifications && specifications.length > 0 && specifications.map((spec, index) => (
                <div key={index} className="specification-row">
                  <input 
                    type="text" 
                    placeholder="Specification name"
                    value={spec.key}
                    onChange={(e) => updateSpecification(index, 'key', e.target.value)}
                  />
                  <input 
                    type="text" 
                    placeholder="Specification value"
                    value={spec.value}
                    onChange={(e) => updateSpecification(index, 'value', e.target.value)}
                  />
                  <button 
                    type="button" 
                    className="remove-spec-btn"
                    onClick={() => removeSpecification(index)}
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button type="button" className="add-spec-btn" onClick={addSpecification}>
                + Add Specification
              </button>
            </div>
          </div>

          {/* Features */}
          <div className="form-section">
            <h3>Key Features</h3>
            <div className="features-section">
              {features && features.length > 0 && features.map((feature, index) => (
                <div key={index} className="feature-row">
                  <input 
                    type="text" 
                    placeholder="Enter feature"
                    value={feature}
                    onChange={(e) => updateFeature(index, e.target.value)}
                  />
                  <button 
                    type="button" 
                    className="remove-feature-btn"
                    onClick={() => removeFeature(index)}
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button type="button" className="add-feature-btn" onClick={addFeature}>
                + Add Feature
              </button>
            </div>
          </div>

          {/* Tags */}
          <div className="form-section">
            <h3>Tags</h3>
            <div className="tags-section">
              {tags && tags.length > 0 && tags.map((tag, index) => (
                <div key={index} className="tag-row">
                  <input 
                    type="text" 
                    placeholder="Enter tag"
                    value={tag}
                    onChange={(e) => updateTag(index, e.target.value)}
                  />
                  <button 
                    type="button" 
                    className="remove-tag-btn"
                    onClick={() => removeTag(index)}
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button type="button" className="add-tag-btn" onClick={addTag}>
                + Add Tag
              </button>
            </div>
          </div>

          {/* Product Variations */}
          <div className="form-section">
            <h3>Product Variations</h3>
            <div className="variations-toggle">
              <input 
                type="checkbox" 
                id="has_variations"
                checked={newProduct.has_variations}
                onChange={(e) => setNewProduct(prev => ({ ...prev, has_variations: e.target.checked }))}
              />
              <label htmlFor="has_variations">This product has variations (e.g., Size, Color, Material)</label>
            </div>

            {newProduct.has_variations && (
              <div className="variations-section">
                {/* Variation Types */}
                <div className="variation-types">
                  <h4>Variation Types</h4>
                  {variationTypes.map((type, typeIndex) => (
                    <div key={typeIndex} className="variation-type-row">
                      <div className="type-inputs">
                        <input 
                          type="text" 
                          placeholder="Variation type (e.g., Size, Color)"
                          value={type.name}
                          onChange={(e) => updateVariationType(typeIndex, 'name', e.target.value)}
                        />
                        <div className="options-section">
                          <label>Options:</label>
                          <div className="options-list">
                            {type.options.map((option, optionIndex) => (
                              <div key={optionIndex} className="option-input-row">
                                <input 
                                  type="text" 
                                  placeholder={`Option ${optionIndex + 1}`}
                                  value={option}
                                  onChange={(e) => updateVariationOption(typeIndex, optionIndex, e.target.value)}
                                />
                                <button 
                                  type="button" 
                                  className="remove-option-btn"
                                  onClick={() => removeVariationOption(typeIndex, optionIndex)}
                                  disabled={type.options.length === 1}
                                >
                                  ×
                                </button>
                              </div>
                            ))}
                            <button 
                              type="button" 
                              className="add-option-btn"
                              onClick={() => addVariationOption(typeIndex)}
                            >
                              + Add Option
                            </button>
                          </div>
                        </div>
                      </div>
                      <button 
                        type="button" 
                        className="remove-variation-type-btn"
                        onClick={() => removeVariationType(typeIndex)}
                      >
                        Remove Type
                      </button>
                    </div>
                  ))}
                  <button type="button" className="add-variation-type-btn" onClick={addVariationType}>
                    + Add Variation Type
                  </button>
                </div>

                {/* Generated Variations */}
                {variations.length > 0 && (
                  <div className="generated-variations">
                    <h4>Generated Variations ({variations.length})</h4>
                    <div className="variations-grid">
                      {variations.map((variation, index) => (
                        <div key={index} className="variation-card">
                          <div className="variation-header">
                            <h5>{variation.name}</h5>
                            <label className="variation-active">
                              <input 
                                type="checkbox" 
                                checked={variation.is_active}
                                onChange={(e) => updateVariation(index, 'is_active', e.target.checked)}
                              />
                              Active
                            </label>
                          </div>
                          
                          <div className="variation-content">
                            <div className="variation-image">
                              <label>Variation Image</label>
                              <input 
                                type="file" 
                                accept="image/*"
                                onChange={(e) => {
                                  const file = e.target.files[0];
                                  if (file) {
                                    handleVariationImageUpload(file, index);
                                  }
                                }}
                              />
                              {variation.image_url && (
                                <div className="variation-image-preview">
                                  <img src={variation.image_url} alt={variation.name} />
                                </div>
                              )}
                            </div>
                            
                            <div className="variation-details">
                              <div className="form-row">
                                <div className="form-group">
                                  <label>Price ($)</label>
                                  <input 
                                    type="number" 
                                    step="0.01" 
                                    placeholder="0.00"
                                    value={variation.price}
                                    onChange={(e) => updateVariation(index, 'price', e.target.value)}
                                  />
                                </div>
                                <div className="form-group">
                                  <label>Original Price ($)</label>
                                  <input 
                                    type="number" 
                                    step="0.01" 
                                    placeholder="0.00"
                                    value={variation.original_price}
                                    onChange={(e) => updateVariation(index, 'original_price', e.target.value)}
                                  />
                                </div>
                              </div>
                              
                              <div className="form-row">
                                <div className="form-group">
                                  <label>Stock</label>
                                  <input 
                                    type="number" 
                                    placeholder="0"
                                    value={variation.stock}
                                    onChange={(e) => updateVariation(index, 'stock', e.target.value)}
                                  />
                                </div>
                                <div className="form-group">
                                  <label>SKU</label>
                                  <input 
                                    type="text" 
                                    placeholder="Variation SKU"
                                    value={variation.sku}
                                    onChange={(e) => updateVariation(index, 'sku', e.target.value)}
                                  />
                                </div>
                              </div>
                              
                              <div className="form-group">
                                <label>Weight</label>
                                <div className="weight-input-group">
                                  <input 
                                    type="number" 
                                    step="0.01" 
                                    placeholder="0.00"
                                    value={variation.weight}
                                    onChange={(e) => updateVariation(index, 'weight', e.target.value)}
                                  />
                                  <select 
                                    value={variation.weight_unit || 'g'}
                                    onChange={(e) => updateVariation(index, 'weight_unit', e.target.value)}
                                  >
                                    <option value="g">Grams (g)</option>
                                    <option value="kg">Kilograms (kg)</option>
                                  </select>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Policies */}
          <div className="form-section">
            <h3>Policies & Information</h3>
            <div className="form-group">
              <label>Warranty</label>
              <input 
                type="text" 
                placeholder="e.g., 1 year warranty"
                value={newProduct.warranty}
                onChange={(e) => setNewProduct(prev => ({ ...prev, warranty: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <label>Shipping Information</label>
              <textarea 
                rows="3" 
                placeholder="Shipping details and costs"
                value={newProduct.shipping_info}
                onChange={(e) => setNewProduct(prev => ({ ...prev, shipping_info: e.target.value }))}
              ></textarea>
            </div>
            <div className="form-group">
              <label>Return Policy</label>
              <textarea 
                rows="3" 
                placeholder="Return and refund policy"
                value={newProduct.return_policy}
                onChange={(e) => setNewProduct(prev => ({ ...prev, return_policy: e.target.value }))}
              ></textarea>
            </div>
          </div>



          {/* SEO */}
          <div className="form-section">
            <h3>SEO Information</h3>
            <div className="form-group">
              <label>Meta Title</label>
              <input 
                type="text" 
                placeholder="SEO title for search engines"
                value={newProduct.meta_title}
                onChange={(e) => setNewProduct(prev => ({ ...prev, meta_title: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <label>Meta Description</label>
              <textarea 
                rows="3" 
                placeholder="SEO description for search engines"
                value={newProduct.meta_description}
                onChange={(e) => setNewProduct(prev => ({ ...prev, meta_description: e.target.value }))}
              ></textarea>
            </div>
            <div className="form-group">
              <label>SEO Keywords</label>
              <input 
                type="text" 
                placeholder="Keywords separated by commas"
                value={newProduct.seo_keywords}
                onChange={(e) => setNewProduct(prev => ({ ...prev, seo_keywords: e.target.value }))}
              />
            </div>
          </div>
          
          <div className="form-actions">
            <button type="button" className="cancel-btn" onClick={() => setActiveSection('products')}>
              Cancel
            </button>
            <button type="button" className="draft-btn" onClick={() => handleSaveAsDraft()}>
              Save as Draft
            </button>
            <button type="submit" className="save-btn">
              Save Product
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  const renderOrders = () => (
    <div className="dashboard-orders">
      <div className="section-header">
        <h2>My Orders</h2>
      </div>
      
      {loading ? (
        <div className="loading">Loading orders...</div>
      ) : (
        <div className="orders-table">
          <table>
            <thead>
              <tr>
                <th>Order ID</th>
                <th>Customer</th>
                <th>Total</th>
                <th>Status</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {orders.map(order => (
                <tr key={order.id}>
                  <td>{order.id}</td>
                  <td>{order.customer}</td>
                  <td>${order.total}</td>
                  <td>
                    <span className={`status-badge ${order.status}`}>
                      {order.status}
                    </span>
                  </td>
                  <td>{order.date}</td>
                  <td>
                    <button className="view-btn">View</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderReports = () => {
    // Calculate sales data
    const totalSales = orders.reduce((sum, order) => sum + (order.total || 0), 0);
    const totalOrders = orders.length;
    const averageOrderValue = totalOrders > 0 ? totalSales / totalOrders : 0;
    
    // Calculate monthly sales for the chart
    const currentDate = new Date();
    const monthlyData = [];
    for (let i = 5; i >= 0; i--) {
      const month = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
      const monthName = month.toLocaleDateString('en-US', { month: 'short' });
      const monthSales = orders.filter(order => {
        const orderDate = new Date(order.created_at);
        return orderDate.getMonth() === month.getMonth() && 
               orderDate.getFullYear() === month.getFullYear();
      }).reduce((sum, order) => sum + (order.total || 0), 0);
      
      monthlyData.push({ month: monthName, sales: monthSales });
    }

    // Get top-selling products
    const productSales = {};
    orders.forEach(order => {
      if (order.order_items && Array.isArray(order.order_items)) {
        order.order_items.forEach(item => {
          const productId = item.product_id || item.id;
          if (productId) {
            productSales[productId] = (productSales[productId] || 0) + (item.quantity || 1);
          }
        });
      }
    });

    const topProducts = products
      .map(product => ({
        ...product,
        sales: productSales[product.id] || 0
      }))
      .sort((a, b) => b.sales - a.sales)
      .slice(0, 5);

    // Calculate status breakdown
    const statusBreakdown = orders.reduce((acc, order) => {
      const status = order.status || 'pending';
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});

    const maxMonthlySales = Math.max(...monthlyData.map(d => d.sales), 1);

    return (
      <div className="dashboard-reports">
        <div className="section-header">
          <h2>📈 Sales Reports & Analytics</h2>
          <p className="section-description">Comprehensive overview of your business performance</p>
        </div>
        
        {/* Key Metrics */}
        <div className="reports-metrics">
          <div className="metric-card total-sales">
            <div className="metric-icon">💰</div>
            <div className="metric-content">
              <h3>Total Sales</h3>
              <div className="metric-value">${totalSales.toFixed(2)}</div>
              <div className="metric-trend positive">+12% from last month</div>
            </div>
          </div>
          
          <div className="metric-card total-orders">
            <div className="metric-icon">🛒</div>
            <div className="metric-content">
              <h3>Total Orders</h3>
              <div className="metric-value">{totalOrders}</div>
              <div className="metric-trend positive">+8% from last month</div>
            </div>
          </div>
          
          <div className="metric-card avg-order">
            <div className="metric-icon">📊</div>
            <div className="metric-content">
              <h3>Avg Order Value</h3>
              <div className="metric-value">${averageOrderValue.toFixed(2)}</div>
              <div className="metric-trend positive">+5% from last month</div>
            </div>
          </div>
          
          <div className="metric-card total-products">
            <div className="metric-icon">📦</div>
            <div className="metric-content">
              <h3>Total Products</h3>
              <div className="metric-value">{products.length}</div>
              <div className="metric-trend neutral">Active listings</div>
            </div>
          </div>
        </div>

        {/* Charts Section */}
        <div className="reports-charts">
          {/* Monthly Sales Chart */}
          <div className="chart-card">
            <h3>📈 Monthly Sales Trend</h3>
            <div className="chart-container">
              <div className="bar-chart">
                {monthlyData.map((data, index) => (
                  <div key={index} className="bar-item">
                    <div 
                      className="bar" 
                      style={{ 
                        height: `${(data.sales / maxMonthlySales) * 100}%`,
                        background: `linear-gradient(45deg, #ff9900, #ff6b35)`
                      }}
                    >
                      <div className="bar-value">${data.sales.toFixed(0)}</div>
                    </div>
                    <div className="bar-label">{data.month}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Order Status Breakdown */}
          <div className="chart-card">
            <h3>📋 Order Status Breakdown</h3>
            <div className="chart-container">
              <div className="status-chart">
                {Object.entries(statusBreakdown).map(([status, count]) => (
                  <div key={status} className="status-item">
                    <div className="status-info">
                      <span className={`status-badge ${status}`}>{status}</span>
                      <span className="status-count">{count} orders</span>
                    </div>
                    <div className="status-bar">
                      <div 
                        className={`status-fill ${status}`}
                        style={{ width: `${(count / totalOrders) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Top Products & Recent Orders */}
        <div className="reports-details">
          <div className="detail-card">
            <h3>🏆 Top Selling Products</h3>
            <div className="top-products-list">
              {topProducts.length > 0 ? topProducts.map((product, index) => (
                <div key={product.id} className="top-product-item">
                  <div className="product-rank">#{index + 1}</div>
                  <div className="product-image">
                    {product.image_url ? (
                      <img src={product.image_url} alt={product.name} />
                    ) : (
                      <div className="no-image">📦</div>
                    )}
                  </div>
                  <div className="product-info">
                    <div className="product-name">{product.name}</div>
                    <div className="product-stats">
                      <span className="sales-count">{product.sales} sold</span>
                      <span className="product-price">${product.price_usd || product.price_gbp || '0.00'}</span>
                    </div>
                  </div>
                </div>
              )) : (
                <div className="no-data">
                  <div className="no-data-icon">📊</div>
                  <p>No sales data available yet</p>
                </div>
              )}
            </div>
          </div>

          <div className="detail-card">
            <h3>🕒 Recent Orders</h3>
            <div className="recent-orders-list">
              {orders.slice(0, 8).map((order) => (
                <div key={order.id} className="recent-order-item">
                  <div className="order-info">
                    <div className="order-id">Order #{order.id}</div>
                    <div className="order-date">
                      {new Date(order.created_at).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </div>
                  </div>
                  <div className="order-details">
                    <div className="order-total">${(order.total || 0).toFixed(2)}</div>
                    <div className={`order-status ${order.status || 'pending'}`}>
                      {order.status || 'pending'}
                    </div>
                  </div>
                </div>
              ))}
              {orders.length === 0 && (
                <div className="no-data">
                  <div className="no-data-icon">🛒</div>
                  <p>No orders found</p>
                </div>
              )}
            </div>
          </div>
        </div>


      </div>
    );
  };

  const renderDispatch = () => (
    <div className="dashboard-dispatch">
      <div className="section-header">
        <h2>Dispatch Settings</h2>
        <p className="section-description">
          Choose how you want to handle order dispatch for your products
        </p>
      </div>
      
      <div className="dispatch-options">
        <div className={`dispatch-option ${dispatchSettings.dispatch_method === 'store' ? 'active' : ''}`}>
          <div className="dispatch-option-header">
            <div className="dispatch-option-icon">🏪</div>
            <div className="dispatch-option-content">
              <h3>Dispatch by Store</h3>
              <p>Handle order dispatch and shipping directly from your store</p>
            </div>
            <div className="dispatch-option-radio">
              <input
                type="radio"
                name="dispatch_method"
                value="store"
                checked={dispatchSettings.dispatch_method === 'store'}
                onChange={() => handleDispatchSettingsUpdate('store')}
              />
            </div>
          </div>
          <div className="dispatch-option-features">
            <ul>
              <li>Full control over shipping process</li>
              <li>Direct customer communication</li>
              <li>Custom packaging options</li>
              <li>Real-time tracking updates</li>
            </ul>
          </div>
        </div>

        <div className={`dispatch-option ${dispatchSettings.dispatch_method === 'infinity' ? 'active' : ''}`}>
          <div className="dispatch-option-header">
            <div className="dispatch-option-icon">🚀</div>
            <div className="dispatch-option-content">
              <h3>Infinity Dispatch</h3>
              <p>Let our platform handle all dispatch and shipping automatically</p>
            </div>
            <div className="dispatch-option-radio">
              <input
                type="radio"
                name="dispatch_method"
                value="infinity"
                checked={dispatchSettings.dispatch_method === 'infinity'}
                onChange={() => handleDispatchSettingsUpdate('infinity')}
              />
            </div>
          </div>
          <div className="dispatch-option-features">
            <ul>
              <li>Automated order processing</li>
              <li>Professional shipping partners</li>
              <li>Reduced operational overhead</li>
              <li>Faster delivery times</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="dispatch-info">
        <div className="info-card">
          <h4>Current Setting</h4>
          <p>
            <strong>Dispatch Method:</strong> {dispatchSettings.dispatch_method === 'store' ? 'Store Dispatch' : 'Infinity Dispatch'}
          </p>
          <p>
            <strong>Status:</strong> {dispatchSettings.is_active ? 'Active' : 'Inactive'}
          </p>
        </div>
      </div>
    </div>
  );

  const renderEditProduct = () => (
    <div className="dashboard-edit-product">
      <div className="section-header">
        <div className="header-content">
          <h2>Edit Product</h2>
          <p className="header-subtitle">Update your product information</p>
        </div>
        <button className="back-btn" onClick={handleCancelEdit}>
          <span className="btn-icon">←</span>
          <span className="btn-text">Back to Products</span>
        </button>
      </div>
      
      <div className="edit-product-form">
        <form onSubmit={handleUpdateProduct}>
          {/* Basic Information */}
          <div className="form-section">
            <h3>Basic Information</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Available Countries</label>
                <div className="countries-section">
                  <div className="country-item">
                    <input 
                      type="checkbox" 
                      id="edit-country-uae"
                      checked={editProduct.countries && editProduct.countries.includes('UAE')}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setEditProduct(prev => ({ 
                            ...prev, 
                            countries: [...(prev.countries || []), 'UAE'] 
                          }));
                        } else {
                          setEditProduct(prev => ({ 
                            ...prev, 
                            countries: (prev.countries || []).filter(country => country !== 'UAE') 
                          }));
                        }
                      }}
                    />
                    <label htmlFor="edit-country-uae">UAE</label>
                  </div>
                  <div className="country-item">
                    <input 
                      type="checkbox" 
                      id="edit-country-usa"
                      checked={editProduct.countries && editProduct.countries.includes('USA')}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setEditProduct(prev => ({ 
                            ...prev, 
                            countries: [...(prev.countries || []), 'USA'] 
                          }));
                        } else {
                          setEditProduct(prev => ({ 
                            ...prev, 
                            countries: (prev.countries || []).filter(country => country !== 'USA') 
                          }));
                        }
                      }}
                    />
                    <label htmlFor="edit-country-usa">USA</label>
                  </div>
                  <div className="country-item">
                    <input 
                      type="checkbox" 
                      id="edit-country-uk"
                      checked={editProduct.countries && editProduct.countries.includes('UK')}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setEditProduct(prev => ({ 
                            ...prev, 
                            countries: [...(prev.countries || []), 'UK'] 
                          }));
                        } else {
                          setEditProduct(prev => ({ 
                            ...prev, 
                            countries: (prev.countries || []).filter(country => country !== 'UK') 
                          }));
                        }
                      }}
                    />
                    <label htmlFor="edit-country-uk">UK</label>
                  </div>
                  <div className="country-item">
                    <input 
                      type="checkbox" 
                      id="edit-country-saudi"
                      checked={editProduct.countries && editProduct.countries.includes('Saudi Arabia')}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setEditProduct(prev => ({ 
                            ...prev, 
                            countries: [...(prev.countries || []), 'Saudi Arabia'] 
                          }));
                        } else {
                          setEditProduct(prev => ({ 
                            ...prev, 
                            countries: (prev.countries || []).filter(country => country !== 'Saudi Arabia') 
                          }));
                        }
                      }}
                    />
                    <label htmlFor="edit-country-saudi">Saudi Arabia</label>
                  </div>
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="edit-name">Product Name *</label>
                <input
                  type="text"
                  id="edit-name"
                  value={editProduct.name || ''}
                  onChange={(e) => setEditProduct(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Enter product name"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="edit-brand">Brand</label>
                <input
                  type="text"
                  id="edit-brand"
                  value={editProduct.brand || ''}
                  onChange={(e) => setEditProduct(prev => ({ ...prev, brand: e.target.value }))}
                  placeholder="Enter brand name"
                />
              </div>
              <div className="form-group">
                <label htmlFor="edit-sku">SKU</label>
                <input
                  type="text"
                  id="edit-sku"
                  value={editProduct.sku || ''}
                  onChange={(e) => setEditProduct(prev => ({ ...prev, sku: e.target.value }))}
                  placeholder="Stock Keeping Unit"
                />
              </div>
              <div className="form-group">
                <label htmlFor="edit-category">Category</label>
                <select
                  id="edit-category"
                  value={editProduct.category || ''}
                  onChange={(e) => setEditProduct(prev => ({ ...prev, category: e.target.value }))}
                  required
                >
                  <option value="">Select Category</option>
                  {categories && categories.length > 0 && categories.map(category => (
                    <option key={category.id} value={category.name}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Pricing */}
          <div className="form-section">
            <h3>Pricing & Inventory</h3>
            <div className="form-grid">
              {/* USD Prices - Show only if USA is selected */}
              {editProduct.countries && editProduct.countries.includes('USA') && (
                <>
                  <div className="form-group">
                    <label htmlFor="edit-price-usd">Current Price (USD) *</label>
                    <input
                      type="number"
                      id="edit-price-usd"
                      value={editProduct.price_usd || ''}
                      onChange={(e) => setEditProduct(prev => ({ ...prev, price_usd: e.target.value }))}
                      placeholder="0.00"
                      step="0.01"
                      min="0"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="edit-original-price-usd">Original Price (USD)</label>
                    <input
                      type="number"
                      id="edit-original-price-usd"
                      value={editProduct.original_price_usd || ''}
                      onChange={(e) => setEditProduct(prev => ({ ...prev, original_price_usd: e.target.value }))}
                      placeholder="0.00"
                      step="0.01"
                      min="0"
                    />
                  </div>
                </>
              )}
              
              {/* GBP Prices - Show only if UK is selected */}
              {editProduct.countries && editProduct.countries.includes('UK') && (
                <>
                  <div className="form-group">
                    <label htmlFor="edit-price-gbp">Current Price (GBP) *</label>
                    <input
                      type="number"
                      id="edit-price-gbp"
                      value={editProduct.price_gbp || ''}
                      onChange={(e) => setEditProduct(prev => ({ ...prev, price_gbp: e.target.value }))}
                      placeholder="0.00"
                      step="0.01"
                      min="0"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="edit-original-price-gbp">Original Price (GBP)</label>
                    <input
                      type="number"
                      id="edit-original-price-gbp"
                      value={editProduct.original_price_gbp || ''}
                      onChange={(e) => setEditProduct(prev => ({ ...prev, original_price_gbp: e.target.value }))}
                      placeholder="0.00"
                      step="0.01"
                      min="0"
                    />
                  </div>
                </>
              )}
              
              {/* AED Prices - Show only if UAE is selected */}
              {editProduct.countries && editProduct.countries.includes('UAE') && (
                <>
                  <div className="form-group">
                    <label htmlFor="edit-price-aed">Current Price (AED) *</label>
                    <input
                      type="number"
                      id="edit-price-aed"
                      value={editProduct.price_aed || ''}
                      onChange={(e) => setEditProduct(prev => ({ ...prev, price_aed: e.target.value }))}
                      placeholder="0.00"
                      step="0.01"
                      min="0"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="edit-original-price-aed">Original Price (AED)</label>
                    <input
                      type="number"
                      id="edit-original-price-aed"
                      value={editProduct.original_price_aed || ''}
                      onChange={(e) => setEditProduct(prev => ({ ...prev, original_price_aed: e.target.value }))}
                      placeholder="0.00"
                      step="0.01"
                      min="0"
                    />
                  </div>
                </>
              )}
              
              {/* KSA Prices - Show only if Saudi Arabia is selected */}
              {editProduct.countries && editProduct.countries.includes('Saudi Arabia') && (
                <>
                  <div className="form-group">
                    <label htmlFor="edit-price-sar">Current Price (KSA) *</label>
                    <input
                      type="number"
                      id="edit-price-sar"
                      value={editProduct.price_sar || ''}
                      onChange={(e) => setEditProduct(prev => ({ ...prev, price_sar: e.target.value }))}
                      placeholder="0.00"
                      step="0.01"
                      min="0"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="edit-original-price-sar">Original Price (KSA)</label>
                    <input
                      type="number"
                      id="edit-original-price-sar"
                      value={editProduct.original_price_sar || ''}
                      onChange={(e) => setEditProduct(prev => ({ ...prev, original_price_sar: e.target.value }))}
                      placeholder="0.00"
                      step="0.01"
                      min="0"
                    />
                  </div>
                </>
              )}
              
              {/* Stock */}
              <div className="form-group">
                <label htmlFor="edit-stock">Stock Quantity *</label>
                <input
                  type="number"
                  id="edit-stock"
                  value={editProduct.stock || ''}
                  onChange={(e) => setEditProduct(prev => ({ ...prev, stock: e.target.value }))}
                  placeholder="0"
                  min="0"
                  required
                />
              </div>
            </div>
          </div>

          {/* Descriptions */}
          <div className="form-section">
            <h3>Product Descriptions</h3>
            <div className="form-group">
              <label htmlFor="edit-description">Short Description</label>
                              <textarea
                  id="edit-description"
                  value={editProduct.description || ''}
                  onChange={(e) => setEditProduct(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Brief product description for listings"
                  rows="3"
                />
              </div>
              <div className="form-group">
                <label htmlFor="edit-long-description">Detailed Description</label>
                <textarea
                  id="edit-long-description"
                  value={editProduct.long_description || ''}
                  onChange={(e) => setEditProduct(prev => ({ ...prev, long_description: e.target.value }))}
                  placeholder="Comprehensive product description for detail page"
                  rows="6"
                />
            </div>
          </div>

          {/* Images */}
          <div className="form-section">
            <h3>Product Images</h3>
            <div className="form-group">
              <label>Main Product Image</label>
              <div className="image-upload-section">
                {editProduct.image_url ? (
                  <div className="current-image">
                    <img src={editProduct.image_url} alt="Current product" />
                    <button 
                      type="button" 
                      className="remove-image-btn"
                      onClick={() => setEditProduct(prev => ({ ...prev, image_url: '' }))}
                    >
                      Remove Image
                    </button>
                  </div>
                ) : (
                  <div className="upload-area">
                    <input
                      type="file"
                      id="edit-image-upload"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          handleEditImageUpload(file);
                        }
                      }}
                      style={{ display: 'none' }}
                    />
                    <label htmlFor="edit-image-upload" className="upload-label">
                      <span className="upload-icon">📷</span>
                      <span>Click to upload image</span>
                    </label>
                  </div>
                )}
                {uploading && (
                  <div className="upload-status">
                    <div className="upload-spinner"></div>
                    <span>Uploading image...</span>
                  </div>
                )}
              </div>
            </div>
            
            <div className="form-group">
              <label>Gallery Images</label>
              <div className="gallery-upload-section">
                <div className="gallery-upload-controls">
                  <input 
                    type="file" 
                    accept="image/*"
                    multiple
                    id="edit-gallery-upload"
                    onChange={(e) => {
                      const files = Array.from(e.target.files);
                      if (files.length > 0) {
                        handleEditGalleryImageUpload(files);
                      }
                    }}
                    style={{ display: 'none' }}
                  />
                  <label htmlFor="edit-gallery-upload" className="upload-btn">
                    📷 Add Images
                  </label>
                  <button 
                    type="button" 
                    className="add-more-images-btn"
                    onClick={() => document.getElementById('edit-gallery-upload').click()}
                  >
                    ➕ Add More Images
                  </button>
                </div>
                {uploading && <p className="upload-status">Uploading gallery images...</p>}
                <div className="gallery-preview">
                  {editProduct.gallery_images && editProduct.gallery_images.map((image, index) => (
                    <div key={index} className="gallery-item">
                      <img src={image} alt={`Gallery ${index + 1}`} />
                      <button 
                        type="button" 
                        className="remove-gallery-btn"
                        onClick={() => removeEditGalleryImage(index)}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  {(!editProduct.gallery_images || editProduct.gallery_images.length === 0) && (
                    <div className="gallery-empty">
                      <span>📷</span>
                      <p>No gallery images added yet</p>
                      <small>Click "Add Images" to upload product photos</small>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Videos */}
          <div className="form-section">
            <h3>Product Videos</h3>
            <div className="form-group">
              <label>Product Video</label>
              <div className="video-upload-section">
                {editProduct.video_url ? (
                  <div className="current-video">
                    <video 
                      controls 
                      width="100%" 
                      height="200"
                      src={editProduct.video_url}
                    >
                      Your browser does not support the video tag.
                    </video>
                    <button 
                      type="button" 
                      className="remove-video-btn"
                      onClick={() => setEditProduct(prev => ({ ...prev, video_url: '' }))}
                    >
                      Remove Video
                    </button>
                  </div>
                ) : (
                  <div className="upload-area">
                    <input
                      type="file"
                      id="edit-video-upload"
                      accept="video/*"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          handleEditVideoUpload(file);
                        }
                      }}
                      style={{ display: 'none' }}
                    />
                    <label htmlFor="edit-video-upload" className="upload-label">
                      <span className="upload-icon">🎥</span>
                      <span>Click to upload video</span>
                    </label>
                  </div>
                )}
                {uploading && (
                  <div className="upload-status">
                    <div className="upload-spinner"></div>
                    <span>Uploading video...</span>
                  </div>
                )}
                <div className="video-info">
                  <p><strong>Supported formats:</strong> MP4, WebM, OGG, MOV</p>
                  <p><strong>Maximum size:</strong> 100MB</p>
                  <p><strong>Recommended:</strong> 720p or higher resolution</p>
                </div>
              </div>
            </div>
            

          </div>

          {/* Product Details */}
          <div className="form-section">
            <h3>Product Details</h3>
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="edit-weight">Weight</label>
                <div className="weight-input-group">
                  <input
                    type="number"
                    id="edit-weight"
                    step="0.01"
                    placeholder="0.00"
                    value={editProduct.weight || ''}
                    onChange={(e) => setEditProduct(prev => ({ ...prev, weight: e.target.value }))}
                  />
                  <select 
                    value={editProduct.weight_unit || 'g'}
                    onChange={(e) => setEditProduct(prev => ({ ...prev, weight_unit: e.target.value }))}
                  >
                    <option value="g">Grams (g)</option>
                    <option value="kg">Kilograms (kg)</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="edit-dimensions">Dimensions (L×W×H)</label>
                <div className="dimensions-input-group">
                  <input
                    type="text"
                    id="edit-dimensions"
                    placeholder="e.g., 10×5×2"
                    value={editProduct.dimensions || ''}
                    onChange={(e) => setEditProduct(prev => ({ ...prev, dimensions: e.target.value }))}
                  />
                  <select
                    id="edit-dimension-unit"
                    value={editProduct.dimension_unit || 'cm'}
                    onChange={(e) => setEditProduct(prev => ({ ...prev, dimension_unit: e.target.value }))}
                  >
                    <option value="cm">Centimeters (cm)</option>
                    <option value="mm">Millimeters (mm)</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="edit-color">Color</label>
                <input
                  type="text"
                  id="edit-color"
                  placeholder="e.g., Red, Blue, Black"
                  value={editProduct.color || ''}
                  onChange={(e) => setEditProduct(prev => ({ ...prev, color: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label htmlFor="edit-material">Material</label>
                <input
                  type="text"
                  id="edit-material"
                  placeholder="e.g., Cotton, Plastic, Metal"
                  value={editProduct.material || ''}
                  onChange={(e) => setEditProduct(prev => ({ ...prev, material: e.target.value }))}
                />
              </div>

            </div>
          </div>

          {/* Specifications */}
          <div className="form-section">
            <h3>Specifications</h3>
            <div className="specifications-section">
              {editProduct.specifications && Object.entries(editProduct.specifications).map(([key, value], index) => (
                <div key={index} className="specification-row">
                  <input 
                    type="text" 
                    placeholder="Specification name"
                    value={key}
                    onChange={(e) => updateEditSpecification(index, 'key', e.target.value)}
                  />
                  <input 
                    type="text" 
                    placeholder="Specification value"
                    value={value}
                    onChange={(e) => updateEditSpecification(index, 'value', e.target.value)}
                  />
                  <button 
                    type="button" 
                    className="remove-spec-btn"
                    onClick={() => removeEditSpecification(index)}
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button type="button" className="add-spec-btn" onClick={addEditSpecification}>
                + Add Specification
              </button>
            </div>
          </div>

          {/* Features */}
          <div className="form-section">
            <h3>Key Features</h3>
            <div className="features-section">
              {editProduct.features && editProduct.features.map((feature, index) => (
                <div key={index} className="feature-row">
                  <input 
                    type="text" 
                    placeholder="Enter feature"
                    value={feature}
                    onChange={(e) => updateEditFeature(index, e.target.value)}
                  />
                  <button 
                    type="button" 
                    className="remove-feature-btn"
                    onClick={() => removeEditFeature(index)}
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button type="button" className="add-feature-btn" onClick={addEditFeature}>
                + Add Feature
              </button>
            </div>
          </div>

          {/* Tags */}
          <div className="form-section">
            <h3>Tags</h3>
            <div className="tags-section">
              {editProduct.tags && editProduct.tags.map((tag, index) => (
                <div key={index} className="tag-row">
                  <input 
                    type="text" 
                    placeholder="Enter tag"
                    value={tag}
                    onChange={(e) => updateEditTag(index, e.target.value)}
                  />
                  <button 
                    type="button" 
                    className="remove-tag-btn"
                    onClick={() => removeEditTag(index)}
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button type="button" className="add-tag-btn" onClick={addEditTag}>
                + Add Tag
              </button>
            </div>
          </div>

          {/* Policies */}
          <div className="form-section">
            <h3>Policies & Information</h3>
            <div className="form-group">
              <label htmlFor="edit-warranty">Warranty</label>
                              <input
                  type="text"
                  id="edit-warranty"
                  placeholder="e.g., 1 year warranty"
                  value={editProduct.warranty || ''}
                  onChange={(e) => setEditProduct(prev => ({ ...prev, warranty: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label htmlFor="edit-shipping-info">Shipping Information</label>
                <textarea
                  id="edit-shipping-info"
                  rows="3"
                  placeholder="Shipping details and costs"
                  value={editProduct.shipping_info || ''}
                  onChange={(e) => setEditProduct(prev => ({ ...prev, shipping_info: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label htmlFor="edit-return-policy">Return Policy</label>
                <textarea
                  id="edit-return-policy"
                  rows="3"
                  placeholder="Return and refund policy"
                  value={editProduct.return_policy || ''}
                  onChange={(e) => setEditProduct(prev => ({ ...prev, return_policy: e.target.value }))}
                />
            </div>
          </div>



          {/* Product Variations */}
          <div className="form-section">
            <h3>Product Variations</h3>
            <div className="variations-toggle">
              <input 
                type="checkbox" 
                id="edit-has-variations"
                checked={editProduct.has_variations}
                onChange={(e) => setEditProduct(prev => ({ ...prev, has_variations: e.target.checked }))}
              />
              <label htmlFor="edit-has-variations">This product has variations (e.g., Size, Color)</label>
            </div>
            
            {editProduct.has_variations && (
              <>
                {/* Variation Types */}
                <div className="variation-types">
                  <h4>Variation Types</h4>
                  {editProduct.variationTypes && editProduct.variationTypes.map((type, typeIndex) => (
                    <div key={typeIndex} className="variation-type-row">
                      <div className="type-inputs">
                        <input 
                          type="text" 
                          placeholder="Variation type (e.g., Size, Color)"
                          value={type.name}
                          onChange={(e) => updateEditVariationType(typeIndex, 'name', e.target.value)}
                        />
                        <div className="options-section">
                          <label>Options:</label>
                          <div className="options-list">
                            {type.options.map((option, optionIndex) => (
                              <div key={optionIndex} className="option-input-row">
                                <input 
                                  type="text" 
                                  placeholder={`Option ${optionIndex + 1}`}
                                  value={option}
                                  onChange={(e) => updateEditVariationOption(typeIndex, optionIndex, e.target.value)}
                                />
                                <button 
                                  type="button" 
                                  className="remove-option-btn"
                                  onClick={() => removeEditVariationOption(typeIndex, optionIndex)}
                                  disabled={type.options.length === 1}
                                >
                                  ×
                                </button>
                              </div>
                            ))}
                            <button 
                              type="button" 
                              className="add-option-btn"
                              onClick={() => addEditVariationOption(typeIndex)}
                            >
                              + Add Option
                            </button>
                          </div>
                        </div>
                      </div>
                      <button 
                        type="button" 
                        className="remove-variation-type-btn"
                        onClick={() => removeEditVariationType(typeIndex)}
                      >
                        Remove Type
                      </button>
                    </div>
                  ))}
                  <button type="button" className="add-variation-type-btn" onClick={addEditVariationType}>
                    + Add Variation Type
                  </button>
                </div>

                {/* Generated Variations */}
                {editProduct.variations && editProduct.variations.length > 0 && (
                  <div className="variations-section">
                    <h4>Product Variations ({editProduct.variations.length})</h4>
                    <div className="variations-grid">
                      {editProduct.variations.map((variation, index) => (
                        <div key={index} className="variation-card">
                          <div className="variation-header">
                            <h5>{variation.combination}</h5>
                            <span className="variation-id">#{index + 1}</span>
                          </div>
                          <div className="variation-content">
                            <div className="variation-image">
                              {variation.image_url ? (
                                <div className="current-variation-image">
                                  <img src={variation.image_url} alt={variation.combination} />
                                  <button 
                                    type="button" 
                                    className="remove-variation-image-btn"
                                    onClick={() => updateEditVariation(index, 'image_url', '')}
                                  >
                                    ×
                                  </button>
                                </div>
                              ) : (
                                <div className="variation-upload-area">
                                  <input
                                    type="file"
                                    accept="image/*"
                                    onChange={(e) => {
                                      const file = e.target.files[0];
                                      if (file) {
                                        handleEditVariationImageUpload(file, index);
                                      }
                                    }}
                                    style={{ display: 'none' }}
                                    id={`edit-variation-image-${index}`}
                                  />
                                  <label htmlFor={`edit-variation-image-${index}`} className="variation-upload-label">
                                    <span>📷</span>
                                    <small>Add Image</small>
                                  </label>
                                </div>
                              )}
                            </div>
                            <div className="variation-details">
                              <div className="form-row">
                                <div className="form-group">
                                  <label>Price ($)</label>
                                  <input
                                    type="number"
                                    step="0.01"
                                    placeholder="0.00"
                                    value={variation.price || ''}
                                    onChange={(e) => updateEditVariation(index, 'price', e.target.value)}
                                  />
                                </div>
                                <div className="form-group">
                                  <label>Stock</label>
                                  <input
                                    type="number"
                                    placeholder="0"
                                    value={variation.stock || ''}
                                    onChange={(e) => updateEditVariation(index, 'stock', e.target.value)}
                                  />
                                </div>
                              </div>
                              <div className="form-row">
                                <div className="form-group">
                                  <label>SKU</label>
                                  <input
                                    type="text"
                                    placeholder="Variation SKU"
                                    value={variation.sku || ''}
                                    onChange={(e) => updateEditVariation(index, 'sku', e.target.value)}
                                  />
                                </div>
                                <div className="form-group">
                                  <label>Weight</label>
                                  <div className="weight-input-group">
                                    <input
                                      type="number"
                                      step="0.01"
                                      placeholder="0.00"
                                      value={variation.weight || ''}
                                      onChange={(e) => updateEditVariation(index, 'weight', e.target.value)}
                                    />
                                    <select 
                                      value={variation.weight_unit || 'g'}
                                      onChange={(e) => updateEditVariation(index, 'weight_unit', e.target.value)}
                                    >
                                      <option value="g">Grams (g)</option>
                                      <option value="kg">Kilograms (kg)</option>
                                    </select>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* SEO */}
          <div className="form-section">
            <h3>SEO Information</h3>
            <div className="form-group">
              <label htmlFor="edit-meta-title">Meta Title</label>
                              <input
                  type="text"
                  id="edit-meta-title"
                  placeholder="SEO title for search engines"
                  value={editProduct.meta_title || ''}
                  onChange={(e) => setEditProduct(prev => ({ ...prev, meta_title: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label htmlFor="edit-meta-description">Meta Description</label>
                <textarea
                  id="edit-meta-description"
                  rows="3"
                  placeholder="SEO description for search engines"
                  value={editProduct.meta_description || ''}
                  onChange={(e) => setEditProduct(prev => ({ ...prev, meta_description: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label htmlFor="edit-seo-keywords">SEO Keywords</label>
                <input
                  type="text"
                  id="edit-seo-keywords"
                  placeholder="Keywords separated by commas"
                  value={editProduct.seo_keywords || ''}
                  onChange={(e) => setEditProduct(prev => ({ ...prev, seo_keywords: e.target.value }))}
                />
            </div>
          </div>
          
          <div className="form-actions">
            <button type="button" className="cancel-btn" onClick={handleCancelEdit}>
              Cancel
            </button>
            <button type="button" className="draft-btn" onClick={() => handleSaveEditAsDraft()}>
              Save as Draft
            </button>
            <button type="submit" className="update-btn">
              Update Product
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  const handleSaveAsDraft = async () => {
    // For drafts, only name is required - everything else is optional
    if (!newProduct.name || newProduct.name.trim() === '') {
      showNotification('Please provide at least a product name to save as draft.', 'error');
      return;
    }

    try {
      const vendorId = localStorage.getItem('vendorId');
      
      // Convert specifications array to object
      const specsObject = {};
      specifications.forEach(spec => {
        if (spec.key && spec.value) {
          specsObject[spec.key] = spec.value;
        }
      });

      // Filter out empty features and tags
      const filteredFeatures = features.filter(feature => feature.trim() !== '');
      const filteredTags = tags.filter(tag => tag.trim() !== '');

      // Helper function to safely parse numeric values
      const parseNumericValue = (value) => {
        if (value === null || value === undefined || value === '' || value === 'undefined' || value === 'null') {
          return null;
        }
        const parsed = parseFloat(value);
        if (isNaN(parsed)) {
          return null;
        }
        return parsed;
      };

      // Helper function to safely parse integer values
      const parseIntValue = (value) => {
        if (value === null || value === undefined || value === '' || value === 'undefined' || value === 'null') {
          return 0;
        }
        const parsed = parseInt(value);
        if (isNaN(parsed)) {
          return 0;
        }
        return parsed;
      };

      const { data, error } = await supabase
        .from('products')
        .insert([
          {
            name: newProduct.name.trim(),
            price_usd: parseNumericValue(newProduct.price_usd),
            price_gbp: parseNumericValue(newProduct.price_gbp),
            price_aed: parseNumericValue(newProduct.price_aed),
            price_sar: parseNumericValue(newProduct.price_sar),
            original_price_usd: parseNumericValue(newProduct.original_price_usd),
            original_price_gbp: parseNumericValue(newProduct.original_price_gbp),
            original_price_aed: parseNumericValue(newProduct.original_price_aed),
            original_price_sar: parseNumericValue(newProduct.original_price_sar),
            category: newProduct.category || '',
            brand: newProduct.brand || '',
            stock: parseIntValue(newProduct.stock),
            sku: newProduct.sku || '',
            description: newProduct.description || '',
            long_description: newProduct.long_description || '',
            image_url: newProduct.image_url || '',
            gallery_images: newProduct.gallery_images || [],
            video_url: newProduct.video_url || '',
            video_thumbnail: newProduct.video_thumbnail || '',
            specifications: specsObject,
            features: filteredFeatures,
            tags: filteredTags,
            weight: parseNumericValue(newProduct.weight),
            weight_unit: newProduct.weight_unit || 'g',
            dimensions: newProduct.dimensions || '',
            dimension_unit: newProduct.dimension_unit || 'cm',
            countries: newProduct.countries || [],
            color: newProduct.color || '',
            material: newProduct.material || '',
            warranty: newProduct.warranty || '',
            shipping_info: newProduct.shipping_info || '',
            return_policy: newProduct.return_policy || '',
            is_featured: newProduct.is_featured || false,
            is_bestseller: newProduct.is_bestseller || false,
            is_new_arrival: newProduct.is_new_arrival || false,
            meta_title: newProduct.meta_title || '',
            meta_description: newProduct.meta_description || '',
            seo_keywords: newProduct.seo_keywords || '',
            has_variations: newProduct.has_variations || false,
            variations: newProduct.has_variations ? variations : [],
            vendor_id: vendorId,
            status: 'draft'
          }
        ])
        .select()
        .single();

      if (error) {
        throw error;
      }

      showNotification('Product saved as draft successfully!', 'success');
      // Reset form and reload products
      setNewProduct({
        name: '',
        price_usd: '',
        price_gbp: '',
        price_aed: '',
        price_sar: '',
        original_price_usd: '',
        original_price_gbp: '',
        original_price_aed: '',
        original_price_sar: '',
        category: '',
        brand: '',
        stock: '',
        sku: '',
        description: '',
        long_description: '',
        image_url: '',
        gallery_images: [],
        video_url: '',
        video_thumbnail: '',
        specifications: {},
        features: [],
        tags: [],
        weight: '',
        weight_unit: 'g',
        dimensions: '',
        dimension_unit: 'cm',
        countries: [],
        color: '',
        material: '',
        warranty: '',
        shipping_info: '',
        return_policy: '',
        is_featured: false,
        is_bestseller: false,
        is_new_arrival: false,
        meta_title: '',
        meta_description: '',
        seo_keywords: '',
        has_variations: false,
        variations: []
      });
      setGalleryImages([]);
      setSpecifications([{ key: '', value: '' }]);
      setFeatures(['']);
      setTags(['']);
      setVariations([]);
      setVariationTypes([]);
      
      setActiveSection('drafts');
      loadVendorData(); // Reload products
      
    } catch (error) {
      console.error('Error saving product as draft:', error);
      showNotification('Error saving product as draft. Please try again.', 'error');
    }
  };

  // Validation function to check if a draft product has all mandatory fields
  const isDraftReadyToPublish = (product) => {
    // Check if product name exists and is not empty
    if (!product.name || product.name.trim() === '') {
      return false;
    }

    // Check if at least one price is provided
    const hasPrice = product.price_usd || product.price_gbp || product.price_aed || product.price_sar;
    if (!hasPrice) {
      return false;
    }

    // Check if stock is provided and is greater than 0
    if (!product.stock || product.stock <= 0) {
      return false;
    }

    // Check if category is provided
    if (!product.category || product.category.trim() === '') {
      return false;
    }

    // Check if description is provided
    if (!product.description || product.description.trim() === '') {
      return false;
    }

    // Check if image is provided
    if (!product.image_url || product.image_url.trim() === '') {
      return false;
    }

    return true;
  };

  const handlePublishDraft = async (product) => {
    // Validate the product before publishing
    if (!isDraftReadyToPublish(product)) {
      showNotification('Cannot publish draft. Please fill in all mandatory fields: Name, Price, Stock, Category, Description, and Image.', 'error');
      return;
    }

    try {
      const { error } = await supabase
        .from('products')
        .update({ status: 'active' })
        .eq('id', product.id);

      if (error) {
        throw error;
      }

      showNotification('Product published successfully!', 'success');
      loadVendorData(); // Reload products
    } catch (error) {
      console.error('Error publishing product:', error);
      showNotification('Error publishing product. Please try again.', 'error');
    }
  };

  const renderDrafts = () => {
    const draftProducts = products.filter(product => product.status === 'draft');
    
    return (
      <div className="dashboard-drafts">
        <div className="section-header">
          <div className="header-content">
            <h2>Draft Products</h2>
            <p className="header-subtitle">Manage your draft products</p>
          </div>
        </div>
        
        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading your drafts...</p>
          </div>
        ) : draftProducts.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📝</div>
            <h3>No Draft Products</h3>
            <p>You don't have any draft products yet</p>
            <button className="empty-cta-btn" onClick={() => setActiveSection('add-product')}>
              Create a Draft
            </button>
          </div>
        ) : (
          <div className="products-container">
            <div className="products-stats">
              <div className="stat-card">
                <div className="stat-icon">📝</div>
                <div className="stat-content">
                  <div className="stat-number">{draftProducts.length}</div>
                  <div className="stat-label">Draft Products</div>
                </div>
              </div>
            </div>
            
            <div className="products-grid">
              {draftProducts.map(product => {
                const isReadyToPublish = isDraftReadyToPublish(product);
                
                return (
                  <div key={product.id} className="product-card-new draft-card">
                    <div className="product-image-section">
                      {product.image_url ? (
                        <img 
                          src={product.image_url} 
                          alt={product.name}
                          className="product-main-image"
                        />
                      ) : (
                        <div className="product-placeholder-new">
                          <span className="placeholder-icon">📦</span>
                        </div>
                      )}
                      <div className="product-badges">
                        <div className="status-badge draft">
                          <span className="status-dot"></span>
                          Draft
                        </div>
                      </div>
                      <div className="product-actions-overlay">
                        {isReadyToPublish && (
                          <button 
                            className="quick-action-btn publish-action" 
                            title="Publish Product"
                            onClick={() => handlePublishDraft(product)}
                          >
                            <svg viewBox="0 0 24 24" fill="currentColor">
                              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                            </svg>
                          </button>
                        )}
                        <button 
                          className="quick-action-btn edit-action" 
                          title="Edit Product"
                          onClick={() => handleEditProduct(product)}
                        >
                          <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                          </svg>
                        </button>
                        <button 
                          className="quick-action-btn view-action" 
                          title="View Details"
                          onClick={() => handleViewProduct(product)}
                        >
                          <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                          </svg>
                        </button>
                        <button 
                          className="quick-action-btn delete-action" 
                          title="Delete Product"
                          onClick={() => handleDeleteProduct(product)}
                        >
                          <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                          </svg>
                        </button>
                      </div>
                    </div>
                    
                    <div className="product-content-new">
                      <div className="product-header-new">
                        <h3 className="product-title-new">{product.name || 'Unnamed Product'}</h3>
                      </div>
                      
                      <div className="product-price-new">
                        <span className="current-price">
                          {(() => {
                            const { symbol, price } = getPrimaryCurrencyAndPrice(product);
                            return `${symbol}${price.toFixed(2)}`;
                          })()}
                        </span>
                      </div>
                      
                      <div className="product-details-new">
                        <div className="detail-row">
                          <div className="detail-item-new">
                            <span className="detail-icon">📦</span>
                            <span className="detail-label-new">Stock:</span>
                            <span className={`detail-value-new ${product.stock <= 10 ? 'low-stock' : ''}`}>
                              {product.stock || 0}
                            </span>
                          </div>
                          <div className="detail-item-new">
                            <span className="detail-icon">🏷️</span>
                            <span className="detail-label-new">Category:</span>
                            <span className="detail-value-new">{product.category || 'Uncategorized'}</span>
                          </div>
                        </div>
                      </div>
                      
                      {!isReadyToPublish && (
                        <div className="draft-warning">
                          <p>⚠️ Complete all mandatory fields to publish</p>
                          <ul>
                            {!product.name && <li>• Product name</li>}
                            {!product.price_usd && !product.price_gbp && !product.price_aed && !product.price_sar && <li>• At least one price</li>}
                            {!product.stock && <li>• Stock quantity</li>}
                            {!product.category && <li>• Category</li>}
                            {!product.description && <li>• Description</li>}
                            {!product.image_url && <li>• Product image</li>}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  const handleSaveEditAsDraft = async () => {
    // For drafts, only name is required - everything else is optional
    if (!editProduct.name || editProduct.name.trim() === '') {
      showNotification('Please provide at least a product name to save as draft.', 'error');
      return;
    }

    try {
      const vendorId = localStorage.getItem('vendorId');
      
      // Convert specifications array to object
      const specsObject = {};
      specifications.forEach(spec => {
        if (spec.key && spec.value) {
          specsObject[spec.key] = spec.value;
        }
      });

      // Filter out empty features and tags
      const filteredFeatures = features.filter(feature => feature.trim() !== '');
      const filteredTags = tags.filter(tag => tag.trim() !== '');

      // Helper function to safely parse numeric values
      const parseNumericValue = (value) => {
        if (value === null || value === undefined || value === '' || value === 'undefined' || value === 'null') {
          return null;
        }
        const parsed = parseFloat(value);
        if (isNaN(parsed)) {
          return null;
        }
        return parsed;
      };

      // Helper function to safely parse integer values
      const parseIntValue = (value) => {
        if (value === null || value === undefined || value === '' || value === 'undefined' || value === 'null') {
          return 0;
        }
        const parsed = parseInt(value);
        if (isNaN(parsed)) {
          return 0;
        }
        return parsed;
      };

      const updateData = {
        name: editProduct.name.trim(),
        price_usd: parseNumericValue(editProduct.price_usd),
        price_gbp: parseNumericValue(editProduct.price_gbp),
        price_aed: parseNumericValue(editProduct.price_aed),
        price_sar: parseNumericValue(editProduct.price_sar),
        original_price_usd: parseNumericValue(editProduct.original_price_usd),
        original_price_gbp: parseNumericValue(editProduct.original_price_gbp),
        original_price_aed: parseNumericValue(editProduct.original_price_aed),
        original_price_sar: parseNumericValue(editProduct.original_price_sar),
        category: editProduct.category || '',
        brand: editProduct.brand || '',
        stock: parseIntValue(editProduct.stock),
        sku: editProduct.sku || '',
        description: editProduct.description || '',
        long_description: editProduct.long_description || '',
        image_url: editProduct.image_url || '',
        gallery_images: editProduct.gallery_images || [],
        video_url: editProduct.video_url || '',
        video_thumbnail: editProduct.video_thumbnail || '',
        specifications: specsObject,
        features: filteredFeatures,
        tags: filteredTags,
        weight: parseNumericValue(editProduct.weight),
        weight_unit: editProduct.weight_unit || 'g',
        dimensions: editProduct.dimensions || '',
        dimension_unit: editProduct.dimension_unit || 'cm',
        countries: editProduct.countries || [],
        color: editProduct.color || '',
        material: editProduct.material || '',
        warranty: editProduct.warranty || '',
        shipping_info: editProduct.shipping_info || '',
        return_policy: editProduct.return_policy || '',
        is_featured: editProduct.is_featured || false,
        is_bestseller: editProduct.is_bestseller || false,
        is_new_arrival: editProduct.is_new_arrival || false,
        meta_title: editProduct.meta_title || '',
        meta_description: editProduct.meta_description || '',
        seo_keywords: editProduct.seo_keywords || '',
        has_variations: editProduct.has_variations || false,
        variations: editProduct.has_variations ? variations : [],
        status: 'draft'
      };

      const { error } = await supabase
        .from('products')
        .update(updateData)
        .eq('id', editingProduct.id);

      if (error) {
        throw error;
      }

      showNotification('Product saved as draft successfully!', 'success');
      
      // Reset form and go back to drafts
      setEditingProduct(null);
      setEditProduct({
        name: '',
        price_usd: '',
        price_gbp: '',
        price_aed: '',
        price_sar: '',
        original_price_usd: '',
        original_price_gbp: '',
        original_price_aed: '',
        original_price_sar: '',
        category: '',
        brand: '',
        stock: '',
        sku: '',
        description: '',
        long_description: '',
        image_url: '',
        gallery_images: [],
        video_url: '',
        video_thumbnail: '',
        specifications: {},
        features: [],
        tags: [],
        weight: '',
        weight_unit: 'g',
        dimensions: '',
        dimension_unit: 'cm',
        countries: [],
        color: '',
        material: '',
        warranty: '',
        shipping_info: '',
        return_policy: '',
        is_featured: false,
        is_bestseller: false,
        is_new_arrival: false,
        meta_title: '',
        meta_description: '',
        seo_keywords: '',
        has_variations: false,
        variations: []
      });
      setGalleryImages([]);
      setSpecifications([{ key: '', value: '' }]);
      setFeatures(['']);
      setTags(['']);
      setVariations([]);
      setVariationTypes([]);
      
      setActiveSection('drafts');
      loadVendorData(); // Reload products
      
    } catch (error) {
      console.error('Error saving product as draft:', error);
      showNotification('Error saving product as draft. Please try again.', 'error');
    }
  };

  if (!vendorInfo) {
    return (
      <div className="vendor-dashboard-page">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="vendor-dashboard-page">
      <div className="vendor-dashboard-container">
        <div className="dashboard-sidebar">
          <div className="vendor-info">
            <h3>{vendorInfo.businessName}</h3>
            <p>{vendorInfo.email}</p>
          </div>
          
          <nav className="dashboard-nav">
            <button 
              className={`nav-item ${activeSection === 'overview' ? 'active' : ''}`}
              onClick={() => setActiveSection('overview')}
            >
              <span>📊</span> Overview
            </button>
            <button 
              className={`nav-item ${activeSection === 'products' ? 'active' : ''}`}
              onClick={() => setActiveSection('products')}
            >
              <span>📦</span> Products
            </button>
            <button 
              className={`nav-item ${activeSection === 'drafts' ? 'active' : ''}`}
              onClick={() => setActiveSection('drafts')}
            >
              <span>📝</span> Drafts
              <span className="drafts-count">({products.filter(p => p.status === 'draft').length})</span>
            </button>
            <button 
              className={`nav-item ${activeSection === 'add-product' ? 'active' : ''}`}
              onClick={() => setActiveSection('add-product')}
            >
              <span>➕</span> Add Product
            </button>
            <button 
              className={`nav-item ${activeSection === 'orders' ? 'active' : ''}`}
              onClick={() => setActiveSection('orders')}
            >
              <span>🛒</span> Orders
            </button>
            <button 
              className={`nav-item ${activeSection === 'dispatch' ? 'active' : ''}`}
              onClick={() => setActiveSection('dispatch')}
            >
              <span>🚚</span> Dispatch
            </button>
            <button 
              className={`nav-item ${activeSection === 'reports' ? 'active' : ''}`}
              onClick={() => setActiveSection('reports')}
            >
              <span>📈</span> Reports
            </button>
          </nav>
          
          <button className="logout-btn" onClick={handleLogout}>
            <span>🚪</span> Logout
          </button>
        </div>

        <div className="dashboard-main">
          <div className="dashboard-content">
            {activeSection === 'overview' && renderOverview()}
            {activeSection === 'products' && renderProducts()}
            {activeSection === 'add-product' && renderAddProduct()}
            {activeSection === 'edit-product' && renderEditProduct()}
            {activeSection === 'orders' && renderOrders()}
            {activeSection === 'dispatch' && renderDispatch()}
            {activeSection === 'reports' && renderReports()}
            {activeSection === 'drafts' && renderDrafts()}
          </div>
        </div>
      </div>
      {notification && renderNotification()}
      {showViewModal && renderViewModal()}
    </div>
  );
};

export default VendorDashboard; 